import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    common: {
      app: {
        name: 'CalculateCO2',
        tagline: 'AI-powered CSRD compliance made simple'
      },
      nav: {
        dashboard: 'Dashboard',
        activities: 'Activities',
        reports: 'Reports',
        settings: 'Settings',
        admin: 'Admin',
        help: 'Help & Support',
        logout: 'Logout',
        periods: 'Periods',
        profile: 'Profile',
        activeReports: 'Active Reports'
      },
      auth: {
        login: 'Login',
        register: 'Get Started',
        signIn: 'Sign In',
        signInTitle: 'Sign in to your account',
        noAccount: "Don't have an account?",
        haveAccount: 'Already have an account?',
        email: 'Email Address',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        rememberMe: 'Remember me',
        forgotPassword: 'Forgot password?',
        signingIn: 'Signing in...',
        createAccount: 'Create Account',
        creatingAccount: 'Creating account...',
        firstName: 'First Name',
        lastName: 'Last Name',
        companyName: 'Company Name',
        preferredLanguage: 'Preferred Language',
        accountSetup: 'Account Setup',
        companyInfo: 'Company Info',
        continue: 'Continue',
        back: 'Back',
        backToHome: 'Back to Home',
        welcomeTitle: 'Welcome to Your',
        esgCommandCenter: 'ESG Command Center',
        welcomeSubtitle: 'Track, measure, and reduce your carbon footprint with precision. CSRD compliance made simple.',
        automatedCalcs: 'Automated emission calculations',
        csrdReporting: 'CSRD-compliant reporting',
        realtimeInsights: 'Real-time sustainability insights',
        startJourney: 'Start Your Journey to',
        carbonNeutrality: 'Carbon Neutrality',
        joinSMEs: 'Join hundreds of European SMEs using CalculateCO2 to automate their ESG reporting and achieve CSRD compliance.',
        regFailed: 'Registration failed. Please try again.',
        unexpectedError: 'An unexpected error occurred. Please try again.',
        signInButton: 'Sign In',
        createAccountButton: 'Create Account',
        sendResetLink: 'Send Reset Link',
        resetPassword: 'Reset Your Password',
        resetDescription: "Enter your email and we'll send you instructions to reset your password.",
        sending: 'Sending...',
        checkEmail: 'Check your email',
        resetLinkSent: "We've sent a password reset link to",
        resetLinkNotReceived: "Didn't receive the email? Check your spam folder or",
        tryAgain: 'try again',
        backToLogin: 'Back to login'
      },
      actions: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        add: 'Add',
        submit: 'Submit',
        close: 'Close',
        confirm: 'Confirm',
        loading: 'Loading...',
        search: 'Search',
        addActivity: 'Add New Activity Data',
        viewReports: 'View Reports',
        generateReport: 'Generate CSRD Report',
        view: 'View',
        remove: 'Remove',
        back: 'Back',
        saving: 'Saving...',
        sending: 'Sending...',
        next: 'Next',
        previous: 'Previous',
        updating: 'Updating...',
        prev: 'Previous',
      },
      common: {
        showing: 'Showing',
        of: 'of',
        noData: 'No data available',
        search: 'Search',
        dropFiles: 'Drop files here',
        dragDropFiles: 'Drag and drop files here, or click to select',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        view: 'View',
        back: 'Back',
        next: 'Next',
        prev: 'Previous',
        previous: 'Previous',
        submit: 'Submit',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        warning: 'Warning',
        info: 'Information',
        confirm: 'Confirm',
        close: 'Close',
        filter: 'Filter',
        export: 'Export',
        import: 'Import',
        download: 'Download',
        upload: 'Upload',
        add: 'Add',
        remove: 'Remove',
        actions: 'Actions',
        status: 'Status',
        name: 'Name',
        description: 'Description',
        date: 'Date',
        time: 'Time',
        type: 'Type',
        total: 'Total',
        yes: 'Yes',
        no: 'No',
        all: 'All',
        none: 'None',
        other: 'Other',
        reactivate: 'Reactivate',
        deactivate: 'Deactivate',
        adding: 'Adding...',
        never: 'Never',
        select: 'Select',
        report: 'Report',
      },
      activities: {
        title: 'Activities',
        subtitle: 'Manage your environmental activity data',
        addActivity: 'Add Activity',
        exportCSV: 'Export CSV',
        noActivities: 'No activities found for this category.',
        addFirstEntry: 'Add your first entry',
        reportingPeriod: 'Reporting Period',
        activityType: 'Activity Type',
        status: 'Status',
        emissions: 'Emissions (MT CO₂e)',
        locationBased: 'Location-Based (MT CO₂e)',
        marketBased: 'Market-Based (MT CO₂e)',
        sourceId: 'Source ID',
        sourceDescription: 'Source Description',
        refrigerantType: 'Gas',
        suppressantType: 'Gas',
        inventoryChange: 'Inventory Change',
      reportingPeriods: {
        title: 'Active Reports',
        subtitle: 'Manage your reporting periods',
        createNew: 'Create New Report',
        label: 'Report Label',
        labelPlaceholder: 'e.g., FY 2024',
        startDate: 'Start Date',
        endDate: 'End Date',
        status: 'Status',
        createSuccess: 'Report created successfully',
        emptyStateTitle: 'Welcome to CalculateCO2!',
        emptyStateText: 'To begin your ESG journey, you need to set up your first <strong>reporting period</strong> (e.g., Fiscal Year 2024).',
        createButton: 'Create Reporting Period',
        deleteConfirm: 'Are you sure you want to delete "{{label}}"? All associated data will be permanently lost.',
        deleteSuccess: 'Report deleted successfully',
        deleteError: 'Failed to delete report',
        createError: 'Failed to create reporting period',
        loadError: 'Failed to load reports',
        noPeriods: 'No Active Reports',
        noPeriodsDescription: "Create your first reporting period to start tracking your organization's carbon emissions.",
        create: '+ Create Report',
        description: 'Manage your organizational reporting periods for carbon accounting',
        standard: 'Standard',
        type: 'Type',
        newPeriod: 'New Report'
      },
      period: {
        types: {
          annual: 'Annual',
          quarterly: 'Quarterly',
          monthly: 'Monthly',
          custom: 'Custom'
        }
      },
      standards: {
        csrd: {
          shortDesc: 'Mandatory for EU companies meeting size criteria',
          note: 'Note:',
          mandatoryInfo: 'CSRD is mandatory for EU companies with ≥250 employees, ≥€50M revenue, or ≥€25M assets. Non-EU companies can use GHG Protocol or ISO 14064.'
        },
        ghg: {
          shortDesc: 'Global voluntary standard for emissions reporting'
        },
        iso: {
          shortDesc: 'International standard for GHG quantification'
        },
        compareStandards: 'Compare Standards',
        comparison: {
          feature: 'Feature',
          region: 'Region',
          typeLabel: 'Type',
          scope3Required: 'Scope 3 Required',
          assurance: 'Assurance',
          bestFor: 'Best For',
          mandatory: 'Mandatory (EU)',
          voluntary: 'Voluntary',
          yes: 'Yes',
          optional: 'Optional',
          partial: 'Partial',
          required: 'Required',
          recommended: 'Recommended',
          eu: 'EU',
          global: 'Global',
          euCompanies: 'EU companies',
          globalReporting: 'Global reporting',
          isoCert: 'ISO certification',
          recommendation: '💡 Recommendation',
          euSMEsTitle: 'For EU SMEs:',
          euSMEsText: 'Start with CSRD as it will be mandatory. CalculateCO2 is designed to meet CSRD requirements by default.',
          globalTitle: 'For global companies:',
          globalText: 'GHG Protocol offers maximum flexibility and is widely recognized internationally.',
          overview: 'Overview',
          whoMustComply: 'Who Must Comply',
          keyRequirements: 'Key Requirements',
          keyPrinciples: 'Key Principles',
          timeline: '⏱️ Timeline',
        }
      },
      validation: {
        required: 'This field is required',
        invalidEmail: 'Invalid email address',
        minLength: 'Minimum {{count}} characters required',
        passwordMismatch: 'Passwords do not match',
        endDateAfterStart: 'End date must be after start date'
      },

        fields: {
          source_id: 'Source ID',
          source_description: 'Source Description',
          source_area_sqft: 'Area (sq ft)',
          fuel_combusted: 'Fuel Combusted',
          fuel_state: 'Fuel State',
          quantity_combusted: 'Quantity Combusted',
          units: 'Units',
          on_road_or_non_road: 'On-Road or Non-Road?',
          vehicle_type: 'Vehicle Type',
          vehicle_year: 'Vehicle Year',
          fuel_usage: 'Fuel Usage',
          miles_traveled: 'Miles Traveled',
          refrigerant_type: 'Gas Type',
          gwp: 'Gas GWP',
          inventory_change: 'Inventory Change (kg)',
          transferred_amount: 'Transferred Amount (kg)',
          capacity_change: 'Capacity Change (kg)',
          new_units_charge: 'New Charge (kg)',
          new_units_capacity: 'New Capacity (kg)',
          existing_units_recharge: 'Recharge (kg)',
          disposed_units_capacity: 'Disposed Capacity (kg)',
          disposed_units_recovered: 'Recovered (kg)',
          equipment_type: 'Equipment Type',
          operating_units_capacity: 'Operating Capacity (kg)',
          months_in_operation: 'Months in Operation',
          facility_description: 'Facility Description',
          suppressant_type: 'Gas Type',
          amount_used: 'Amount Used',
          amount_units: 'Units',
          inventory_change_lb: 'Inventory Change (lb)',
          transferred_amount_lb: 'Transferred (lb)',
          capacity_change_lb: 'Capacity Change (lb)',
          new_units_charge_lb: 'New Charge (lb)',
          new_units_capacity_lb: 'New Capacity (lb)',
          existing_units_recharge_lb: 'Recharge (lb)',
          disposed_units_capacity_lb: 'Disposed Capacity (lb)',
          disposed_units_recovered_lb: 'Recovered (lb)',
          unit_capacity_lb: 'Capacity (lb)',
          gas_type: 'Gas Type',
          amount_purchased: 'Amount Purchased',
          co2e: 'CO2 Equivalent',
          facility_location: 'eGRID Subregion',
          kwh_purchased: 'Electricity Purchased (kWh)',
          market_based_co2_factor: 'Market-Based CO2 Factor',
          market_based_ch4_factor: 'Market-Based CH4 Factor',
          market_based_n2o_factor: 'Market-Based N2O Factor',
          fuel_type: 'Fuel Type',
          boiler_efficiency: 'Boiler Efficiency (%)',
          location_based_co2_factor: 'Location-Based CO2 Factor',
          location_based_ch4_factor: 'Location-Based CH4 Factor',
          location_based_n2o_factor: 'Location-Based N2O Factor',
          hotel_name: 'Hotel Name',
          city_country: 'City/Country',
          hotel_category: 'Category',
          num_nights: 'Number of Nights',
          num_rooms: 'Number of Rooms',
          commute_mode: 'Commute Mode',
          distance_per_trip_km: 'Distance Per Trip (km)',
          commute_days_per_year: 'Commute Days Per Year',
          num_commuters: 'Number of Commuters',
          vehicle_miles: 'Vehicle-Miles',
          short_ton_miles: 'Short Ton-Miles',
          waste_type: 'Waste Material',
          disposal_method: 'Disposal Method',
          amount: 'Amount',
          offset_description: 'Project Description',
          scope_category: 'Scope/Category',
          amount_mtco2e: 'Offsets Purchased (MT CO2e)',
          project_description: 'Project Description',
          flightLength: 'Flight Length'
        },
        recordAmendment: 'Record Amendment',
        newEnvironmentalEntry: 'New Environmental Entry',
        reportingContext: 'Reporting Context',
        targetAuditPeriod: 'Target Audit Period',
        selectedPeriod: 'Selected Period',
        autoAssigned: 'Auto-assigned',
        noReportingPeriodError: 'No reporting period specified. Please navigate from Active Reports to add activities.',
        primaryMetricData: 'Primary Metric Data',
        saveActivity: 'Save Activity',
        commitChanges: 'Commit Changes',
        persistRecord: 'Persist Record',
        auditLabel: 'Environmental Audit',
        operationalTitle: 'operational activities.',
        vehicleType: 'Vehicle Type',
        vehicleMiles: 'Vehicle-Miles',
        shortTonMiles: 'Short Ton-Miles',
        distanceKm: 'Distance (km)',
        weightTons: 'Weight (tons)',
        offsetId: 'ID',
        offsetsPurchased: 'Offsets Purchased (MT CO₂e)',
        egridSubregion: 'eGRID Subregion',
        kwhPurchased: 'kWh Purchased',
        fuelType: 'Fuel Type',
        efficiencyPct: 'Efficiency (%)',
        purchasedMMBtu: 'Purchased (MMBtu)',
        shipment: 'Shipment',
        transportMode: 'Mode',
        activityDescription: 'Description',
        backToChecklist: 'Back to Activities Checklist',
        inventoryChangeKg: 'Inventory Change (kg)',
        transferredKg: 'Transferred (kg)',
        capacityChangeKg: 'Capacity Change (kg)',
        newChargeKg: 'New Charge (kg)',
        newCapacityKg: 'New Capacity (kg)',
        rechargeKg: 'Recharge (kg)',
        equipmentType: 'Equipment Type',
        opCapacityKg: 'Op. Capacity (kg)',
        inventoryChangeLb: 'Inventory Change (lb)',
        transferredLb: 'Transferred (lb)',
        capacityChangeLb: 'Capacity Change (lb)',
        newChargeLb: 'New Charge (lb)',
        rechargeLb: 'Recharge (lb)',
        disposedCapLb: 'Disposed Cap. (lb)',
        recoveredLb: 'Recovered (lb)',
        capLb: 'Capacity (lb)',
        purchasedAmountLb: 'Purchased Amount (lb)',
        hotelName: 'Hotel Name',
        hotelCategory: 'Category',
        nights: 'Nights',
        rooms: 'Rooms',
        commuteMode: 'Commute Mode',
        numCommuters: 'Commuters',
        distancePerTripKm: 'Distance/Trip (km)',
        distanceMiles: 'Distance (miles)',
        flightLength: 'Flight Length',
        wasteType: 'Waste Type',
        disposalMethod: 'Disposal Method',
        amount: 'Amount',
        gasType: 'Gas Type',
        projectDescription: 'Project Description',
        scopeCategory: 'Scope / Category',
        createSuccess: 'Activity saved successfully',
        updateSuccess: 'Activity updated successfully',
        createFailed: 'Failed to create activity',
        updateFailed: 'Failed to update activity',
        missingFields: 'Missing required fields: {{fields}}',
        currentCategory: 'Operational Category',
        selectType: 'Select Operational Data Stream',
        boundarySettingsInfo: 'Reporting boundaries active.',
        editSettings: 'Configure',
        notFound: 'Activity not found',
        vehicleYear: 'Vehicle Year',
        fuelCombusted: 'Fuel Combusted',
        fuelState: 'Fuel State',
        quantityCombusted: 'Quantity Combusted',
        units: 'Units',
        co2Equivalent: 'CO\u2082 Equivalent'
      },
      period: {
        types: {
          annual: 'Annual',
          quarterly: 'Quarterly',
          monthly: 'Monthly',
          custom: 'Custom'
        }
      },
      checklist: {
        backToReports: 'Back to Active Reports',
        activities: 'Activities',
        configureSources: 'Configure data for each enabled emission source below.',
        editQuestions: 'Edit Questions',
        generateReport: 'Generate Report',
        noActivitiesEnabled: 'No activities enabled',
        noActivitiesDesc: 'You need to answer the boundary questions to enable emission sources for tracking.',
        goToQuestions: 'Go to Boundary Questions',
        readyTitle: 'Ready to generate your report?',
        readyDesc: 'Review your entries and see your total carbon footprint before generating the final compliance report.',
        fetchError: 'Failed to load activities',
        sources: 'sources'
      },
      activityMetadata: {
        stationary_combustion: { label: 'Stationary Combustion', description: 'On-site fuel combustion (boilers, furnaces)' },
        mobile_sources: { label: 'Mobile Sources', description: 'Owned or leased vehicles' },
        refrigeration_ac_material_balance: { label: 'Refrigeration & AC', description: 'Fugitive emissions from cooling systems' },
        refrigeration_ac_simplified_material_balance: { label: 'Refrigeration & AC (Simplified)', description: 'Fugitive emissions' },
        refrigeration_ac_screening_method: { label: 'Refrigeration & AC (Screening)', description: 'Fugitive emissions' },
        fire_suppression_material_balance: { label: 'Fire Suppression', description: 'Emissions from fire suppressants' },
        fire_suppression_simplified_material_balance: { label: 'Fire Suppression (Simplified)', description: 'Fire suppressants' },
        fire_suppression_screening_method: { label: 'Fire Suppression (Screening)', description: 'Fire suppressants' },
        purchased_gases: { label: 'Purchased Gases', description: 'Industrial gases used in processes' },
        electricity: { label: 'Purchased Electricity', description: 'Purchased electricity for own use' },
        steam: { label: 'Purchased Steam', description: 'Purchased steam, heat, or cooling' },
        business_travel_personal_car: { label: 'Business Travel (Car)', description: 'Employee-owned vehicle travel' },
        business_travel_rail_bus: { label: 'Business Travel (Rail/Bus)', description: 'Public transit business travel' },
        business_travel_air: { label: 'Business Travel (Air)', description: 'Air travel for business' },
        business_travel_hotel: { label: 'Hotel Stays', description: 'Business hotel accommodations' },
        employee_commuting_personal_car: { label: 'Employee Commuting (Car)', description: 'Commuting in personal vehicles' },
        employee_commuting_public_transport: { label: 'Employee Commuting (Public)', description: 'Commuting via public transit' },
        upstream_trans_dist_vehicle_miles: { label: 'Upstream Trans & Dist', description: 'Transportation of purchased goods' },
        upstream_trans_dist_ton_miles: { label: 'Upstream Trans & Dist (Ton-Miles)', description: 'Large scale transportation' },
        waste: { label: 'Waste Generated', description: 'Disposal and treatment of waste' },
        offsets: { label: 'Carbon Offsets', description: 'Verified emission reductions' }
      },

      dashboard: {
        welcome: 'Welcome back,',
        overview: 'Your aggregated emissions overview across all reporting period(s)',
        activeReports: 'Active Reports',
        newReport: '+ New Report',
        aggregatedView: 'Aggregated Data View',
        aggregatedDescription: 'This dashboard shows your total emissions combined across all reporting periods.',
        excellentPerformance: 'Excellent Performance',
        excellentDescription: 'Your emissions are well below the industry baseline',
        roomForImprovement: 'Room for Improvement',
        roomForImprovementDescription: 'Your emissions are near or slightly above industry baseline',
        actionRequired: 'Action Required',
        actionRequiredDescription: 'Your emissions are significantly above industry baseline',
        totalCarbonFootprint: 'Total Carbon Footprint',
        scope1: 'Scope 1 (Direct)',
        scope2: 'Scope 2 (Energy)',
        scope3: 'Scope 3 (Value Chain)',
        scope1Short: 'Scope 1',
        scope2Short: 'Scope 2',
        scope3Short: 'Scope 3',
        direct: 'Direct',
        location: 'Location',
        market: 'Market',
        supplyChain: 'Supply Chain',
        carbonDashboard: 'Carbon Dashboard',
        loading: 'Loading your carbon data...',
        startJourney: 'Start Your Carbon Journey',
        startJourneyDescription: 'Create your first reporting period to begin tracking and reducing your organization\'s carbon footprint.',
        createFirstReport: 'Create Your First Report',
        subtitle: 'Track your carbon emissions and CSRD compliance progress',
        totalEmissions: 'Total Emissions',
        quickActions: 'Quick Actions',
        completionStatus: 'Completion Status',
        dataEntry: 'Data Entry',
        csrdCompliance: 'CSRD Compliance',
        reportGeneration: 'Report Generation',
        dataQuality: 'Data Quality Status',
        complete: 'Complete',
        inProgress: 'In Progress',
        missingData: 'Missing Data',
        trafficLightRating: 'Traffic Light Rating',
        activities: 'Activities',
        directEmissions: 'Direct Emissions',
        purchasedEnergy: 'Purchased Energy',
        ratingGreenTitle: 'Green - Excellent',
        ratingGreenDesc: '✓ Your emissions are 25% or less above industry baseline',
        ratingYellowTitle: 'Yellow - Needs Attention',
        ratingYellowDesc: '⚠️ Your emissions are 25-75% above industry baseline',
        ratingRedTitle: 'Red - Action Required',
        ratingRedDesc: '✗ Your emissions are more than 75% above baseline. Immediate action needed',
        recommendationsTitle: '💡 Recommendations',
        recScope1: '• Optimize fleet vehicles and reduce fuel consumption',
        recScope2: '• Switch to renewable energy sources for purchased electricity',
        recScope3: '• Reduce business travel through virtual meetings',
        mtCo2e: 'MT CO₂e'
      },
      reportingPeriod: {
        createSuccess: 'Report created successfully',
        title: 'Reporting Periods',
        subtitle: 'Manage and create carbon reporting cycles',
        startDate: 'Start Date',
        endDate: 'End Date',
        status: 'Status',
        actions: 'Actions',
        newPeriod: 'New Reporting Period'
      },
      settings: {
        companySettings: 'Company Settings',
        companySubtitle: 'Manage your company profile',
        updateSuccess: 'Settings updated successfully',
        loadError: 'Failed to load company info',
        updateError: 'Failed to update company info',
        basicInfo: 'Basic Information',
        companyName: 'Company Name',
        companyInfo: 'Company Information',
        industry: 'Industry',
        website: 'Website',
        logoUrl: 'Logo URL',
        contactInfo: 'Contact Information',
        email: 'Email',
        phone: 'Phone',
        address: 'Address',
        streetAddress: 'Street Address',
        city: 'City',
        postalCode: 'Postal Code',
        country: 'Country',
        profile: 'Profile Settings',
        profileSubtitle: 'Manage your personal information and preferences',
        personalInfo: 'Personal Information',
        emailReadOnly: 'Email cannot be changed',
        firstName: 'First Name',
        lastName: 'Last Name',
        language: 'Language',
        changePassword: 'Change Password',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        updatePassword: 'Update Password',
        savePreferences: 'Save Preferences',
        notifications: 'Notifications',
        emailNotifications: 'Email Notifications',
        emailNotificationsDesc: 'Receive email updates',
        pushNotifications: 'Push Notifications',
        pushNotificationsDesc: 'Receive push notifications',
        activityReminders: 'Activity Reminders',
        activityRemindersDesc: 'Receive activity reminders',
        reportReady: 'Report Ready',
        reportReadyDesc: 'Receive report ready notifications',
        reportingStandards: 'Reporting Standards',
        reportingStandardsDescription: 'Select the reporting standards your organization follows for carbon accounting and emissions reporting.',
        organizationalBoundary: 'Organizational Boundary',
        boundaryDescription: 'Define your organizational scope, subsidiaries, and consolidation approach for emissions reporting.',
        configureBoundary: 'Configure Boundary Questions',
        weeklyDigest: 'Weekly Digest',
        weeklyDigestDesc: 'Receive weekly digest',
        profileTitle: 'Profile Settings',
        profileUpdated: 'Profile updated!',
        failedLoadProfile: 'Failed to load profile',
        failedUpdateProfile: 'Failed to update profile',
        passwordPlaceholder: 'Leave blank to keep current password',
        companyInfoTitle: 'Company Information',
        companyInfoUpdated: 'Company info updated!',
        failedLoadCompany: 'Failed to load company info',
        failedUpdateCompany: 'Failed to update company info',
        userMgmtTitle: 'User Management',
        failedLoadUsers: 'Failed to load users',
        failedAddUser: 'Failed to add user',
        userRegistered: 'User registered and email sent!',
        failedUpdateRole: 'Failed to update role',
        failedRemoveUser: 'Failed to remove user',
        addUserBtn: 'Add User',
        removeBtn: 'Remove',
        removeUserConfirm: 'Remove this user?',
        selectIndustry: 'Select Industry',
        industryManufacturing: 'Manufacturing',
        industryTechnology: 'Technology',
        industryRetail: 'Retail',
        industryHealthcare: 'Healthcare',
        industryFinance: 'Finance',
        industryEnergy: 'Energy',
        industryTransportation: 'Transportation',
        industryOther: 'Other',
        saveBtn: 'Save',
        loadingText: 'Loading...',
      },
      users: {
        title: 'User Management',
        subtitle: 'Manage team members and permissions',
        inviteUser: 'Invite User',
        addUser: 'Add User',
        firstName: 'First Name',
        lastName: 'Last Name',
        company: 'Company',
        password: 'Password',
        name: 'Name',
        email: 'Email',
        role: 'Role',
        lastLogin: 'Last Login',
        noUsers: 'No users found',
        confirmRemove: 'Are you sure you want to remove this user?',
        confirmDeactivate: 'Are you sure you want to deactivate this user?',
        deactivateSuccess: 'User deactivated successfully',
        reactivateSuccess: 'User reactivated successfully',
        removeSuccess: 'User removed successfully',
        roleUpdateSuccess: 'User role updated successfully',
        inviteSuccess: 'User invitation sent successfully',
        sendInvite: 'Send Invitation',
        confirmDelete: 'Are you sure you want to permanently remove this user from the company?',
        deleteSuccess: 'User removed successfully',
        failedLoad: 'Failed to load users',
        userAddedSuccess: 'User added and credentials sent via email',
        failedAdd: 'Failed to add user',
        failedUpdateRole: 'Failed to update role',
        failedDeactivate: 'Failed to deactivate user',
        failedDelete: 'Failed to delete user',
        failedReactivate: 'Failed to reactivate user',
        roleViewer: 'Viewer - Read only access',
        roleEditor: 'Editor - Can add/edit data',
        roleAdmin: 'Admin - Full access',
        minPassword: 'Min. 8 characters',
        roles: {
          company_admin: 'Company Admin',
          editor: 'Editor',
          viewer: 'Viewer',
          internal_admin: 'Internal Admin'
        }
      },
      boundary: {
        welcomeTitle: 'Welcome to Boundary Setting',
        welcomeDesc: 'Let\'s determine which emissions sources are relevant for your organization. This helps us tailor the platform to your specific needs.',
        chooseIndustry: 'Choose Your Industry (Optional)',
        chooseIndustryDesc: 'Select your industry for recommended settings, or skip and customize manually.',
        skipManual: 'Skip and Customize Manually',
        scope1Title: 'Scope 1: Direct Emissions',
        scope1Desc: 'Emissions from sources owned or controlled by your organization.',
        scope23Title: 'Scope 2 & 3: Indirect Emissions',
        scope23Desc: 'Emissions from purchased energy and your value chain.',
        scope2SectionTitle: 'Scope 2: Energy Indirect',
        scope3SectionTitle: 'Scope 3: Value Chain',
        offsetsSectionTitle: 'Carbon Offsets',
        reviewTitle: 'Review Your Selection',
        reviewDesc: 'Here\'s a summary of your organizational boundaries. You can go back to make changes.',
        noScope1: 'No Scope 1 modules selected',
        noScope2: 'No Scope 2 modules selected',
        noScope3: 'No Scope 3 modules selected',
        offsetEnabled: 'Offset tracking enabled',
        offsetDisabled: 'Offset tracking disabled',
        noteUpdate: 'Note: You can always update these settings later from the Reporting Periods page.',
        examples: 'Examples:',
        stepIndustry: 'Industry',
        stepScope1: 'Scope 1',
        stepScope23: 'Scope 2 & 3',
        stepReview: 'Review',
        saving: 'Saving...',
        saveFinish: 'Save and Finish',
        back: 'Back',
        next: 'Next',
        activityTypeLabel: 'Activity Type:',
        yes: 'YES',
        no: 'NO',
        requiredLabel: 'Required',
        orgTitle: 'Organizational Boundary Settings',
        orgDesc: 'Configure which emission categories are applicable to your organization. These settings will filter available activity types.',
        loadError: 'Failed to load boundary settings',
        saveSuccess: 'Boundary settings saved successfully',
        saveError: 'Failed to save boundary settings',
        saveBtn: 'Save Boundary Settings',
        howItWorks: 'How Boundary Settings Work',
        howItem1: 'Required questions are always enabled and cannot be changed (Scope 1 & Scope 2)',
        howItem2: 'Scope 3 questions can be enabled/disabled based on your organizational boundary',
        howItem3: 'Activities will only appear in the system if their corresponding boundary question is answered "YES"',
        howItem4: 'Changes take effect immediately after saving',
      },
      activityDetails: {
        activityRecord: 'Activity Record',
        entryDetails: 'Entry Details',
        totalFootprint: 'Total Footprint',
        created: 'Created',
        lastUpdated: 'Last Updated',
      },
      // ...
      help: {
        standards: 'Standards & Frameworks',
        scopes: 'Emission Scopes',
        terms: 'Glossary',
        activities: 'Activity Types',
        dataQuality: 'Data Quality',
        title: 'Help & Documentation',
        subtitle: 'Learn about emissions reporting standards, terminology, and best practices',
        topics: 'Topics',
        standardsTitle: 'Reporting Standards & Frameworks',
        whoMustComply: 'Who Must Comply?',
        keyRequirements: 'Key Requirements',
        keyPrinciples: 'Key Principles',
        timelineLabel: 'Timeline:',
        scopesTitle: 'Emission Scopes Explained',
        categories: 'Categories',
        commonExamples: 'Common Examples',
        whyItMatters: 'Why It Matters',
        glossaryTitle: 'Glossary of Terms',
        gwpValues: 'Common GWP Values (100-year)',
        trustedSources: 'Trusted Sources',
        activityGuide: 'Activity Types Guide',
        dataNeed: 'Data Needed:',
        tipsLabel: 'Tips:',
        dataQualityLevels: 'Data Quality Levels',
        qualityLabel: 'Quality:',
        examplesLabel: 'Examples:',
        improveTips: 'Tips for Improving Data Quality',
        loadingContent: 'Loading help content...',
        failedLoad: 'Failed to load help content',
      },
      reports: {
        title: 'Reports',
        subtitle: 'Generate and view emissions reports',
        generateReport: 'Generate Report',
        generateSubtitle: 'Configure and generate your emissions report',
        generateSuccess: 'Report generated successfully',
        reportName: 'Report Name',
        type: 'Type',
        period: 'Period',
        generatedAt: 'Generated At',
        status: 'Status',
        download: 'Download',
        noReports: 'No reports found',
        viewReport: 'View Report',
        totalEmissions: 'Total Emissions',
        scope1: 'Scope 1',
        scope2: 'Scope 2',
        scope3: 'Scope 3',
        scope1Emissions: 'Scope 1 Emissions',
        scope2Emissions: 'Scope 2 Emissions',
        scope3Emissions: 'Scope 3 Emissions',
        emissionsTrends: 'Emissions Trends',
        scopeBreakdown: 'Scope Breakdown',
        mtCO2e: 'MT CO2e',
        company: 'Company',
        timeline: 'Timeline',
        selectPeriod: 'Select Reporting Period',
        loadingPeriods: 'Loading periods...',
        noPeriods: 'No reporting periods found.',
        managePeriods: 'Manage Reporting Periods',
        selectPeriodHint: 'Please select a reporting period to generate a report for.',
        reportStatus: 'Report Status',
        reportPeriodLabel: 'Report Period',
        paid: 'Paid',
        generatedStatus: 'Generated',
        fullPdfExport: 'Full PDF Export',
        pdfExportDesc: 'Includes CSRD compliance, audit trails, and multi-format exports.',
        unlockAccess: 'Unlock Full Access →',
        oneTimePayment: 'One-time payment. Multi-user access included.',
        downloadPdf: 'Download PDF',
        downloadCsv: 'Download CSV',
        reportDetails: 'Report Details',
        standardLabel: 'Standard',
        includeCharts: 'Include Charts',
        detailedBreakdown: 'Detailed Breakdown',
        loadingData: 'Loading data...',
        awaitingData: 'Awaiting Data',
        addActivitiesHint: 'Add activities to generate your emissions summary.',
        directLabel: 'Direct',
        indirectEnergy: 'Indirect Energy',
        contractual: 'Contractual',
        supplyChainLabel: 'Supply Chain',
        totalImpact: 'Total Impact',
        confidential: 'Confidential',
        emissionsReport: 'Emissions Report',
        liveDraft: 'Live Draft',
        activityBreakdown: 'Emissions by Activity Type',
        activityType: 'Activity Type',
        emissionsCol: 'Emissions (MT CO₂e)',
        countCol: 'Count',
        sustainabilityScore: 'Sustainability Score',
        currentRating: 'Current Rating',
        goodPerformance: 'Good sustainability performance',
        roomForImprovementText: 'Room for improvement',
        needsAttention: 'Needs immediate attention',
        keyRecommendations: 'Key Recommendations',
        auditTrail: 'Audit Trail',
        certificationSection: 'Certification',
        signedBy: 'Signed by:',
        reportDate: 'Date:',
        history: 'Report History',
        historySubtitle: 'View and download your generated reports',
        generateNew: 'Generate New Report',
        filterByPeriod: 'Filter by Period:',
        allPeriods: 'All Periods',
        noReportsFound: 'No Reports Found',
        noReportsAll: 'You haven\'t generated any reports yet.',
        noReportsPeriod: 'No reports found for the selected period.',
        generateFirst: 'Generate Your First Report',
        totalReports: 'Total Reports',
        pdfReports: 'PDF Reports',
        csvReports: 'CSV/Excel Reports',
        totalSpent: 'Total Spent',
        deleteReport: 'Delete',
        deleteConfirmMsg: 'Are you sure you want to delete this report?',
        loadingHistory: 'Loading report history...',
        colGeneratedDate: 'Generated Date',
        colPeriod: 'Reporting Period',
        colType: 'Type',
        colPayment: 'Payment',
        colAmount: 'Amount',
        colActions: 'Actions',
        statusPaid: 'Paid',
        statusPending: 'Pending',
        statusFailed: 'Failed',
        statusRefunded: 'Refunded',
        notFound: 'Report not found',
        paymentSuccess: 'Payment successful! Your report is ready.',
        paymentCancelled: 'Payment was cancelled.',
        downloadSuccess: '{{format}} downloaded successfully',
        paymentRequired: 'Payment required to download this report',
        paymentProcessingRetry: 'Payment may still be processing. Please try again in a moment.',
      },
      messages: {
        success: 'Success!',
        error: 'Error occurred',
        loading: 'Loading...',
        noData: 'No data available',
        confirmDelete: 'Are you sure you want to delete this?'
      },
      validation: {
        required: 'This field is required',
        endDateAfterStart: 'End date must be after start date',
        invalidEmail: 'Invalid email address',
        passwordMismatch: 'Passwords do not match',
        minLength: 'Minimum {{count}} characters required',
        maxLength: 'Maximum {{count}} characters allowed'
      },
      status: {
        active: 'Active',
        locked: 'Locked',
        archived: 'Archived',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        completed: 'Completed',
        in_progress: 'In Progress',
        calculated: 'Calculated',
        draft: 'Draft',
        closed: 'Closed'
      },
      reportingPeriods: {
        title: 'Active Reports',
        subtitle: 'Manage your reporting periods',
        createNew: 'Create New Report',
        newPeriod: 'New Report',
        noActiveReports: 'No Active Reports',
        noActiveDesc: "Create your first reporting period to start tracking your organization's carbon emissions.",
        loadingReports: 'Loading reports...',
        editBoundaryTooltip: 'Edit Boundary Questions',
        deleteReportTooltip: 'Delete Report',
        label: 'Period Label',
        startDate: 'Start Date',
        endDate: 'End Date',
        status: 'Status',
        createSuccess: 'Reporting period created successfully',
        emptyStateTitle: 'Welcome to CalculateCO2!',
        emptyStateText: 'To start your ESG journey, you need to set up your first <strong>Reporting Period</strong> (e.g., Fiscal Year 2024).',
        createButton: 'Create Reporting Period',
        deleteConfirm: 'Are you sure you want to delete "{{label}}"? All associated data will be permanently lost.',
        deleteSuccess: 'Report deleted successfully',
        deleteError: 'Failed to delete report',
        createError: 'Failed to create reporting period',
        loadError: 'Failed to load reports',
        noPeriods: 'No Active Reports',
        noPeriodsDescription: "Create your first reporting period to start tracking your organization's carbon emissions.",
        create: '+ Create Report',
        description: 'Manage your organizational reporting periods for carbon accounting',
        standard: 'Standard',
        type: 'Type'
      },
      periodTypes: {
        annual: 'Annual',
        quarterly: 'Quarterly',
        monthly: 'Monthly',
        custom: 'Custom'
      },
      reportingStandards: {
        GHG_PROTOCOL: 'GHG Protocol',
        CSRD: 'CSRD',
        ISO_14064: 'ISO 14064'
      },
      landing: {
        hero: {
          badge: 'CSRD & ESG Compliant',
          title: 'AI-Powered',
          subtitle: 'ESG Compliance',
          titleSuffix: 'Made Simple',
          description: 'Automate your carbon footprint calculations and ESG reporting with European Environment Agency accuracy. Built for SMEs navigating CSRD requirements.',
          ctaStart: 'Start Free Trial',
          ctaDemo: 'View Demo',
          ctaDashboard: 'Go to Dashboard',
          trustCompanies: 'Companies',
          trustAccuracy: 'Accuracy',
          trustSupport: 'Support'
        },
        features: {
          badge: 'Platform Features',
          title: 'Everything You Need for',
          titleHighlight: ' ESG Excellence',
          description: 'Built specifically for European businesses to meet CSRD requirements efficiently and accurately.',
          automated: {
            title: 'Automated Calculations',
            desc: 'Enter your activity data and our AI automatically calculates CO₂e using the latest EEA emission factors with precision.'
          },
          csrd: {
            title: 'CSRD Compliant Reports',
            desc: 'Generate audit-ready reports aligned with Corporate Sustainability Reporting Directive standards instantly.'
          },
          scope: {
            title: 'Complete Scope Coverage',
            desc: 'Comprehensive tracking of Scope 1, 2 & 3 emissions including supply chain calculation modules.'
          }
        },
        howItWorks: {
          badge: 'Simple Process',
          title: 'Get Started in ',
          titleHighlight: 'Three Steps',
          description: 'From sign-up to compliance reports in minutes, not months.',
          step1: {
            title: 'Create Account',
            desc: 'Sign up and set your company profile. Define reporting boundaries with our guided questionnaire.'
          },
          step2: {
            title: 'Enter Activities',
            desc: 'Log your business activities—energy, travel, waste. Our AI matches the right emission factors automatically.'
          },
          step3: {
            title: 'Generate Reports',
            desc: 'Get instant CSRD-compliant reports with actionable insights to reduce emissions and demonstrate compliance.'
          }
        },
        about: {
          badge: 'About CalculateCO2',
          title: 'Empowering Sustainable',
          titleHighlight: ' Business Decisions',
          desc1: 'CalculateCO2 is a climate-tech company on a mission to empower SMEs across Europe to take control of their carbon footprint. Our team combines expertise in sustainability, data science, and software engineering.',
          desc2: 'We believe every business, regardless of size, deserves access to accurate tools for measuring, managing, and reducing environmental impact. CalculateCO2 is committed to transparency, innovation, and supporting the transition to a low-carbon economy.',
          stats: {
            emissions: 'Emissions Tracked',
            satisfaction: 'Customer Satisfaction',
            countries: 'EU Countries',
            savings: 'Avg. Cost Savings'
          }
        },
        cta: {
          title: 'Ready to Simplify Your',
          titleHighlight: ' ESG Compliance?',
          description: 'Join hundreds of European SMEs already using CalculateCO2 to automate their carbon footprint reporting and meet CSRD requirements with confidence.',
          ctaTrial: 'Start Your Free Trial',
          ctaDemo: 'Schedule a Demo',
          ctaDashboard: 'Go to Dashboard'
        },
        footer: {
          rights: '© {{year}} CalculateCO2. All rights reserved.',
          features: 'Features',
          howItWorks: 'How it Works',
          about: 'About'
        }
      },
      activityTypes: {
        mobile_combustion: 'Mobile Combustion',
        stationary_combustion: 'Stationary Combustion',
        purchased_electricity: 'Purchased Electricity',
        transportation: 'Transportation',
        waste_generated: 'Waste Generated',
        business_travel: 'Business Travel',
        employee_commuting: 'Employee Commuting',
        leased_assets: 'Leased Assets',
        capital_goods: 'Capital Goods',
        mobile_sources: 'Mobile Sources',
        refrigeration_ac: 'Refrigeration & AC',
        refrigeration_ac_material_balance: 'Refrigeration & AC (Material Balance)',
        refrigeration_ac_simplified_material_balance: 'Refrigeration & AC (Simplified)',
        refrigeration_ac_screening_method: 'Refrigeration & AC (Screening)',
        fire_suppression: 'Fire Suppression',
        fire_suppression_material_balance: 'Fire Suppression (Material Balance)',
        fire_suppression_simplified_material_balance: 'Fire Suppression (Simplified)',
        fire_suppression_screening_method: 'Fire Suppression (Screening)',
        purchased_gases: 'Purchased Gases',
        electricity: 'Electricity',
        steam: 'Steam/Heating/Cooling',
        business_travel_air: 'Business Travel - Air',
        business_travel_rail: 'Business Travel - Rail',
        business_travel_road: 'Business Travel - Road',
        business_travel_hotel: 'Business Travel - Hotel',
        business_travel_personal_car: 'Business Travel - Personal Vehicle',
        business_travel_rail_bus: 'Business Travel - Rail or Bus',
        commuting: 'Employee Commuting',
        employee_commuting_personal_car: 'Employee Commuting - Personal Vehicle',
        employee_commuting_public_transport: 'Employee Commuting - Public Transport',
        transportation_distribution: 'Transportation & Distribution',
        upstream_trans_dist_vehicle_miles: 'Upstream Trans & Dist - Vehicle-Miles',
        upstream_trans_dist_ton_miles: 'Upstream Trans & Dist - Ton-Miles',
        waste: 'Waste Disposal',
        offsets: 'Carbon Offsets'
      },
      scopes: {
        Scope1: 'Scope 1',
        Scope2: 'Scope 2',
        Scope3: 'Scope 3',
        Other: 'Other'
      },
      fields: {
        source_id: 'Source ID',
        source_description: 'Source Description',
        source_area_sqft: 'Source Area (sq ft)',
        fuel_combusted: 'Fuel Combusted',
        fuel_state: 'Fuel State',
        quantity_combusted: 'Quantity Combusted',
        units: 'Units',
        on_road_or_non_road: 'On-Road or Non-Road?',
        vehicle_type: 'Vehicle Type',
        vehicle_year: 'Vehicle Year',
        fuel_usage: 'Fuel Usage',
        miles_traveled: 'Miles Traveled',
        refrigerant_type: 'Gas',
        gwp: 'Gas GWP',
        inventory_change: 'Inventory Change (kg)',
        transferred_amount: 'Transferred Amount (kg)',
        capacity_change: 'Capacity Change (kg)',
        new_units_charge: 'New Units Charge (kg)',
        new_units_capacity: 'New Units Capacity (kg)',
        existing_units_recharge: 'Existing Units Recharge (kg)',
        disposed_units_capacity: 'Disposed Units Capacity (kg)',
        disposed_units_recovered: 'Disposed Units Recovered (kg)',
        equipment_type: 'Equipment Type',
        operating_units_capacity: 'Operating Units Capacity (kg)',
        months_in_operation: 'Months in Operation',
        facility_description: 'Facility Description',
        suppressant_type: 'Suppressant Type',
        amount_used: 'Amount Used',
        amount_units: 'Units',
        inventory_change_lb: 'Inventory Change (lb)',
        transferred_amount_lb: 'Transferred Amount (lb)',
        capacity_change_lb: 'Capacity Change (lb)',
        new_units_charge_lb: 'New Units - Charge (lb)',
        new_units_capacity_lb: 'New Units - Capacity (lb)',
        existing_units_recharge_lb: 'Existing Units - Recharge (lb)',
        disposed_units_capacity_lb: 'Disposed Units - Capacity (lb)',
        disposed_units_recovered_lb: 'Disposed Units - Recovered (lb)',
        unit_capacity_lb: 'Unit Capacity (lb)',
        gas_type: 'Gas Type',
        amount_purchased: 'Amount Purchased',
        co2e: 'CO2 Equivalent (lb)',
        facility_location: 'eGRID Subregion',
        kwh_purchased: 'Electricity Purchased (kWh)',
        market_based_co2_factor: 'Market-Based CO2 Factor (lb/MWh)',
        market_based_ch4_factor: 'Market-Based CH4 Factor (lb/MWh)',
        market_based_n2o_factor: 'Market-Based N2O Factor (lb/MWh)',
        fuel_type: 'Fuel Type',
        boiler_efficiency: 'Boiler Efficiency (%)',
        location_based_co2_factor: 'Location-Based CO2 Factor (kg/MMBtu)',
        location_based_ch4_factor: 'Location-Based CH4 Factor (g/MMBtu)',
        location_based_n2o_factor: 'Location-Based N2O Factor (g/MMBtu)',
        vehicle_miles: 'Vehicle-Miles',
        short_ton_miles: 'Short Ton-Miles',
        hotel_name: 'Hotel Name',
        city_country: 'City/Country',
        hotel_category: 'Hotel Category',
        num_nights: 'Number of Nights',
        num_rooms: 'Number of Rooms',
        commute_mode: 'Commute Mode',
        distance_per_trip_km: 'Distance Per Trip (km)',
        commute_days_per_year: 'Commute Days Per Year',
        num_commuters: 'Number of Commuters',
        waste_type: 'Waste Material',
        disposal_method: 'Disposal Method',
        amount: 'Weight',
        offset_description: 'Project Description',
        scope_category: 'Offset Scope/Category',
        amount_mtco2e: 'Offsets Purchased (MT CO₂e)'
      }
    }
  },
  de: {
    common: {
      app: {
        name: 'CalculateCO2',
        tagline: 'KI-gestützte CSRD-Compliance leicht gemacht'
      },
      nav: {
        dashboard: 'Übersicht',
        activities: 'Aktivitäten',
        reports: 'Berichte',
        settings: 'Einstellungen',
        admin: 'Admin',
        help: 'Hilfe & Support',
        logout: 'Abmelden',
        periods: 'Zeiträume',
        profile: 'Profil',
        activeReports: 'Aktive Berichte'
      },
      auth: {
        login: 'Anmelden',
        register: 'Jetzt starten',
        signIn: 'Anmelden',
        signInTitle: 'Melden Sie sich an',
        noAccount: 'Noch kein Konto?',
        haveAccount: 'Bereits ein Konto?',
        email: 'E-Mail-Adresse',
        password: 'Passwort',
        confirmPassword: 'Passwort bestätigen',
        rememberMe: 'Angemeldet bleiben',
        forgotPassword: 'Passwort vergessen?',
        signingIn: 'Anmeldung...',
        createAccount: 'Konto erstellen',
        creatingAccount: 'Konto wird erstellt...',
        firstName: 'Vorname',
        lastName: 'Nachname',
        companyName: 'Firmenname',
        preferredLanguage: 'Bevorzugte Sprache',
        accountSetup: 'Kontoeinrichtung',
        companyInfo: 'Unternehmensinfo',
        continue: 'Weiter',
        back: 'Zurück',
        backToHome: 'Zurück zur Startseite',
        welcomeTitle: 'Willkommen in Ihrem',
        esgCommandCenter: 'ESG Command Center',
        welcomeSubtitle: 'Verfolgen, messen und reduzieren Sie Ihren CO2-Fußabdruck mit Präzision. CSRD-Konformität einfach gemacht.',
        automatedCalcs: 'Automatisierte Emissionsberechnungen',
        csrdReporting: 'CSRD-konforme Berichterstattung',
        realtimeInsights: 'Echtzeit-Einblicke in die Nachhaltigkeit',
        startJourney: 'Starten Sie Ihre Reise zur',
        carbonNeutrality: 'CO2-Neutralität',
        joinSMEs: 'Schließen Sie sich hunderten europäischen KMUs an, die CalculateCO2 nutzen, um ihre ESG-Berichterstattung zu automatisieren und die CSRD-Konformität zu erreichen.',
        regFailed: 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
        unexpectedError: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
        signInButton: 'Anmelden',
        createAccountButton: 'Konto erstellen',
        sendResetLink: 'Link senden',
        resetPassword: 'Passwort zurücksetzen',
        resetDescription: 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen Anweisungen zum Zurücksetzen Ihres Passworts.',
        sending: 'Wird gesendet...',
        checkEmail: 'Prüfen Sie Ihre E-Mails',
        resetLinkSent: 'Wir haben einen Link zum Zurücksetzen des Passworts an',
        resetLinkNotReceived: 'E-Mail nicht erhalten? Prüfen Sie Ihren Spam-Ordner oder',
        tryAgain: 'erneut versuchen',
        backToLogin: 'Zurück zur Anmeldung'
      },
      actions: {
        save: 'Speichern',
        cancel: 'Abbrechen',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        add: 'Hinzufügen',
        submit: 'Absenden',
        close: 'Schließen',
        confirm: 'Bestätigen',
        loading: 'Lädt...',
        search: 'Suchen',
        addActivity: 'Neue Aktivitätsdaten hinzufügen',
        viewReports: 'Berichte anzeigen',
        generateReport: 'CSRD-Bericht erstellen',
        view: 'Anzeigen',
        createReportingPeriod: 'Berichtszeitraum erstellen',
        viewReports: 'Berichte anzeigen',
        addActivity: 'Aktivität hinzufügen',
        remove: 'Entfernen',
        back: 'Zurück',
        saving: 'Speichert...',
        sending: 'Sendet...',
        next: 'Weiter',
        previous: 'Zurück',
        updating: 'Aktualisiert...',
        prev: 'Zurück',
      },
      common: {
        showing: 'Anzeigen',
        of: 'von',
        noData: 'Keine Daten verfügbar',
        search: 'Suchen',
        dropFiles: 'Dateien hier ablegen',
        dragDropFiles: 'Dateien hierher ziehen oder klicken',
        save: 'Speichern',
        cancel: 'Abbrechen',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        view: 'Anzeigen',
        back: 'Zurück',
        next: 'Weiter',
        prev: 'Zurück',
        previous: 'Zurück',
        submit: 'Absenden',
        loading: 'Lädt...',
        error: 'Fehler',
        success: 'Erfolg',
        warning: 'Warnung',
        info: 'Information',
        confirm: 'Bestätigen',
        close: 'Schließen',
        filter: 'Filtern',
        export: 'Exportieren',
        import: 'Importieren',
        download: 'Herunterladen',
        upload: 'Hochladen',
        add: 'Hinzufügen',
        remove: 'Entfernen',
        actions: 'Aktionen',
        status: 'Status',
        name: 'Name',
        description: 'Beschreibung',
        date: 'Datum',
        time: 'Zeit',
        type: 'Typ',
        total: 'Gesamt',
        yes: 'Ja',
        no: 'Nein',
        all: 'Alle',
        none: 'Keine',
        other: 'Andere',
        reactivate: 'Reaktivieren',
        deactivate: 'Deaktivieren',
        adding: 'Wird hinzugefügt...',
        never: 'Nie',
        select: 'Auswählen',
        report: 'Bericht',
      },
      activities: {
        title: 'Aktivitäten',
        subtitle: 'Verwalten Sie Ihre Umweltdaten',
        addActivity: 'Aktivität hinzufügen',
        exportCSV: 'Exportieren (CSV)',
        noActivities: 'Keine Aktivitäten in dieser Kategorie gefunden.',
        addFirstEntry: 'Ersten Eintrag hinzufügen',
        notFound: 'Aktivität nicht gefunden',
        activityDetails: 'Aktivitätsdetails',
        basicInfo: 'Grundinformationen',
        emissionsSummary: 'Emissionszusammenfassung',
        metadata: 'Metadaten',
        createdAt: 'Erstellt am',
        updatedAt: 'Aktualisiert am',
        attachments: 'Anhänge',
        reportingPeriod: 'Berichtszeitraum',
        activityType: 'Aktivitätstyp',
        status: 'Status',
        emissions: 'Emissionen (MT CO₂e)',
        locationBased: 'Standortbasiert (MT CO₂e)',
        marketBased: 'Marktbasiert (MT CO₂e)',
        sourceId: 'Quellen-ID',
        sourceDescription: 'Quellbeschreibung',
        refrigerantType: 'Gasart',
        suppressantType: 'Gasart',
        inventoryChange: 'Bestandsveränderung',
        fuelCombusted: 'Verbrannter Kraftstoff',
        fuelState: 'Kraftstoffzustand',
        quantityCombusted: 'Verbrannte Menge',
        units: 'Einheiten',
        vehicleType: 'Fahrzeugtyp',
        vehicleYear: 'Fahrzeugjahr',
        fuelUsage: 'Kraftstoffverbrauch',
        fetchError: 'Aktivitäten konnten nicht geladen werden',
        boundaryRequiredTitle: 'Grenzfragen erforderlich',
        boundaryRequiredText: 'Bitte beantworten Sie die Grenzfragen, um Aktivitäten hinzuzufügen.',
        goToBoundary: 'Zu den Grenzfragen',
      },
      dashboard: {
        welcome: 'Willkommen zurück,',
        overview: 'Ihre aggregierte Emissionsübersicht über alle Berichtszeiträume',
        activeReports: 'Aktive Berichte',
        newReport: '+ Neuer Bericht',
        aggregatedView: 'Aggregierte Datenansicht',
        aggregatedDescription: 'Dieses Dashboard zeigt Ihre Gesamtemissionen kombiniert über alle Berichtszeiträume.',
        excellentPerformance: 'Hervorragende Leistung',
        excellentDescription: 'Ihre Emissionen liegen deutlich unter dem Branchendurchschnitt',
        roomForImprovement: 'Verbesserungspotenzial',
        roomForImprovementDescription: 'Ihre Emissionen liegen nahe bei oder leicht über dem Branchendurchschnitt',
        actionRequired: 'Handlungsbedarf',
        actionRequiredDescription: 'Ihre Emissionen liegen deutlich über dem Branchendurchschnitt',
        totalCarbonFootprint: 'Gesamter CO2-Fußabdruck',
        scope1: 'Scope 1 (Direkt)',
        scope2: 'Scope 2 (Energie)',
        scope3: 'Scope 3 (Wertschöpfungskette)',
        scope1Short: 'Scope 1',
        scope2Short: 'Scope 2',
        scope3Short: 'Scope 3',
        direct: 'Direkt',
        location: 'Standort',
        market: 'Markt',
        supplyChain: 'Lieferkette',
        carbonDashboard: 'Carbon-Dashboard',
        loading: 'Lade Ihre Carbon-Daten...',
        startJourney: 'Beginnen Sie Ihre Carbon-Reise',
        startJourneyDescription: 'Erstellen Sie Ihren ersten Berichtszeitraum, um den CO2-Fußabdruck Ihres Unternehmens zu verfolgen und zu reduzieren.',
        createFirstReport: 'Ersten Bericht erstellen',
        subtitle: 'Verfolgen Sie Ihre CO₂-Emissionen und CSRD-Compliance-Fortschritte',
        totalEmissions: 'Gesamtemissionen',
        quickActions: 'Schnellaktionen',
        completionStatus: 'Abschlussstatus',
        dataEntry: 'Dateneingabe',
        csrdCompliance: 'CSRD-Compliance',
        reportGeneration: 'Berichtserstellung',
        dataQuality: 'Datenqualitätsstatus',
        complete: 'Abgeschlossen',
        inProgress: 'In Bearbeitung',
        missingData: 'Fehlende Daten',
        trafficLightRating: 'Ampelbewertung',
        activities: 'Aktivitäten',
        directEmissions: 'Direkte Emissionen',
        purchasedEnergy: 'Gekaufte Energie',
        ratingGreenTitle: 'Grün - Ausgezeichnet',
        ratingGreenDesc: '✓ Ihre Emissionen liegen 25% oder weniger über dem Branchenbasiswert',
        ratingYellowTitle: 'Gelb - Verbesserungsbedarf',
        ratingYellowDesc: '⚠️ Ihre Emissionen liegen 25-75% über dem Branchenbasiswert',
        ratingRedTitle: 'Rot - Handlungsbedarf',
        ratingRedDesc: '✗ Ihre Emissionen liegen mehr als 75% über dem Basiswert. Sofortige Maßnahmen erforderlich',
        recommendationsTitle: '💡 Empfehlungen',
        recScope1: '• Fuhrpark optimieren und Kraftstoffverbrauch senken',
        recScope2: '• Auf erneuerbare Energiequellen für eingekauften Strom umsteigen',
        recScope3: '• Geschäftsreisen durch virtuelle Meetings reduzieren',
        mtCo2e: 'MT CO₂e'
      },
      reportingPeriod: {
        createSuccess: 'Bericht erfolgreich erstellt',
        title: 'Berichtszeiträume',
        subtitle: 'Verwalten und Erstellen von Carbon-Berichtszyklen',
        startDate: 'Startdatum',
        endDate: 'Enddatum',
        status: 'Status',
        actions: 'Aktionen',
        newPeriod: 'Neuer Berichtszeitraum'
      },
      settings: {
        companySettings: 'Unternehmenseinstellungen',
        companySubtitle: 'Verwalten Sie Ihr Unternehmensprofil',
        updateSuccess: 'Einstellungen erfolgreich aktualisiert',
        loadError: 'Unternehmensinfo konnte nicht geladen werden',
        updateError: 'Unternehmensinfo konnte nicht aktualisiert werden',
        basicInfo: 'Grundinformationen',
        companyName: 'Firmenname',
        companyInfo: 'Unternehmensinformationen',
        industry: 'Branche',
        website: 'Webseite',
        logoUrl: 'Logo-URL',
        contactInfo: 'Kontaktinformationen',
        email: 'E-Mail',
        phone: 'Telefon',
        address: 'Adresse',
        streetAddress: 'Straße',
        city: 'Stadt',
        postalCode: 'Postleitzahl',
        country: 'Land',
        profile: 'Profileinstellungen',
        profileSubtitle: 'Verwalten Sie Ihre persönlichen Informationen und Präferenzen',
        personalInfo: 'Persönliche Informationen',
        emailReadOnly: 'E-Mail kann nicht geändert werden',
        firstName: 'Vorname',
        lastName: 'Nachname',
        language: 'Sprache',
        changePassword: 'Passwort ändern',
        currentPassword: 'Aktuelles Passwort',
        newPassword: 'Neues Passwort',
        confirmPassword: 'Passwort bestätigen',
        updatePassword: 'Passwort aktualisieren',
        savePreferences: 'Einstellungen speichern',
        notifications: 'Benachrichtigungen',
        emailNotifications: 'E-Mail-Benachrichtigungen',
        emailNotificationsDesc: 'Erhalten Sie E-Mail-Updates',
        pushNotifications: 'Push-Benachrichtigungen',
        pushNotificationsDesc: 'Erhalten Sie Push-Benachrichtigungen',
        activityReminders: 'Aktivitätserinnerungen',
        activityRemindersDesc: 'Erhalten Sie Aktivitätserinnerungen',
        reportReady: 'Bericht bereit',
        reportReadyDesc: 'Erhalten Sie Benachrichtigungen, wenn Berichte bereit sind',
        weeklyDigest: 'Wöchentliche Zusammenfassung',
        weeklyDigestDesc: 'Erhalten Sie eine wöchentliche Zusammenfassung',
        profileTitle: 'Profileinstellungen',
        profileUpdated: 'Profil aktualisiert!',
        failedLoadProfile: 'Profil konnte nicht geladen werden',
        failedUpdateProfile: 'Profil konnte nicht aktualisiert werden',
        passwordPlaceholder: 'Leer lassen, um aktuelles Passwort zu behalten',
        companyInfoTitle: 'Unternehmensinformationen',
        companyInfoUpdated: 'Unternehmensinformationen aktualisiert!',
        failedLoadCompany: 'Unternehmensinformationen konnten nicht geladen werden',
        failedUpdateCompany: 'Unternehmensinformationen konnten nicht aktualisiert werden',
        userMgmtTitle: 'Benutzerverwaltung',
        failedLoadUsers: 'Benutzer konnten nicht geladen werden',
        failedAddUser: 'Benutzer konnte nicht hinzugefügt werden',
        userRegistered: 'Benutzer registriert und E-Mail gesendet!',
        failedUpdateRole: 'Rolle konnte nicht aktualisiert werden',
        failedRemoveUser: 'Benutzer konnte nicht entfernt werden',
        addUserBtn: 'Benutzer hinzufügen',
        removeBtn: 'Entfernen',
        removeUserConfirm: 'Diesen Benutzer entfernen?',
        selectIndustry: 'Branche auswählen',
        industryManufacturing: 'Fertigung',
        industryTechnology: 'Technologie',
        industryRetail: 'Einzelhandel',
        industryHealthcare: 'Gesundheitswesen',
        industryFinance: 'Finanzen',
        industryEnergy: 'Energie',
        industryTransportation: 'Transport',
        industryOther: 'Sonstiges',
        saveBtn: 'Speichern',
        loadingText: 'Laden...',
        reportingStandards: 'Berichtsstandards',
        reportingStandardsDescription: 'Wählen Sie die Berichtsstandards, die Ihr Unternehmen für die CO2-Bilanzierung und Emissionsberichterstattung befolgt.',
        organizationalBoundary: 'Organisatorische Grenzen',
        boundaryDescription: 'Definieren Sie Ihren organisatorischen Geltungsbereich, Tochtergesellschaften und den Konsolidierungsansatz für die Emissionsberichterstattung.',
        configureBoundary: 'Grenzen konfigurieren',
        compareStandards: 'Standards vergleichen',
        csrd: {
          shortDesc: 'Verpflichtend für EU-Unternehmen, die Größenkriterien erfüllen',
          note: 'Hinweis:',
          mandatoryInfo: 'CSRD ist verpflichtend für EU-Unternehmen mit ≥250 Mitarbeitern, ≥50 Mio. € Umsatz oder ≥25 Mio. € Bilanzsumme. Nicht-EU-Unternehmen können das GHG Protocol oder ISO 14064 verwenden.'
        },
        ghg: {
          shortDesc: 'Weltweiter freiwilliger Standard für die Emissionsberichterstattung'
        },
        iso: {
          shortDesc: 'Internationaler Standard zur Quantifizierung von Treibhausgasen'
        },
        fields: {
          source_id: 'Quellen-ID',
          source_description: 'Quellenbeschreibung',
          source_area_sqft: 'Fläche (sq ft)',
          fuel_combusted: 'Brennstoffart',
          fuel_state: 'Aggregatzustand',
          quantity_combusted: 'Verbrauchte Menge',
          units: 'Einheiten',
          on_road_or_non_road: 'On-Road oder Non-Road?',
          vehicle_type: 'Fahrzeugtyp',
          vehicle_year: 'Fahrzeugjahr',
          fuel_usage: 'Kraftstoffverbrauch',
          miles_traveled: 'Gefahrene Meilen',
          refrigerant_type: 'Gasart',
          gwp: 'GWP des Gases',
          inventory_change: 'Bestandsänderung (kg)',
          transferred_amount: 'Übertragene Menge (kg)',
          capacity_change: 'Kapazitätsänderung (kg)',
          new_units_charge: 'Neue Füllung (kg)',
          new_units_capacity: 'Neue Kapazität (kg)',
          existing_units_recharge: 'Nachfüllung (kg)',
          disposed_units_capacity: 'Entsorgte Kapazität (kg)',
          disposed_units_recovered: 'Rückgewonnen (kg)',
          equipment_type: 'Gerätetyp',
          operating_units_capacity: 'Betriebskapazität (kg)',
          months_in_operation: 'Betriebsmonate',
          facility_description: 'Einrichtungsbeschreibung',
          suppressant_type: 'Gasart',
          amount_used: 'Verbrauchte Menge',
          amount_units: 'Einheiten',
          inventory_change_lb: 'Bestandsänderung (lb)',
          transferred_amount_lb: 'Übertragen (lb)',
          capacity_change_lb: 'Kapazitätsänderung (lb)',
          new_units_charge_lb: 'Neue Füllung (lb)',
          new_units_capacity_lb: 'Neue Kapazität (lb)',
          existing_units_recharge_lb: 'Nachfüllung (lb)',
          disposed_units_capacity_lb: 'Entsorgte Kapazität (lb)',
          disposed_units_recovered_lb: 'Rückgewonnen (lb)',
          unit_capacity_lb: 'Kapazität (lb)',
          gas_type: 'Gasart',
          amount_purchased: 'Kaufmenge',
          co2e: 'CO2-Äquivalent',
          facility_location: 'eGRID-Subregion',
          kwh_purchased: 'Gekaufter Strom (kWh)',
          market_based_co2_factor: 'Marktbasiert CO2-Faktor',
          market_based_ch4_factor: 'Marktbasiert CH4-Faktor',
          market_based_n2o_factor: 'Marktbasiert N2O-Faktor',
          fuel_type: 'Brennstoffart',
          boiler_efficiency: 'Kesseleffizienz (%)',
          location_based_co2_factor: 'Standortbasiert CO2-Faktor',
          location_based_ch4_factor: 'Standortbasiert CH4-Faktor',
          location_based_n2o_factor: 'Standortbasiert N2O-Faktor',
          hotel_name: 'Hotelname',
          city_country: 'Stadt/Land',
          hotel_category: 'Kategorie',
          num_nights: 'Anzahl der Nächte',
          num_rooms: 'Anzahl der Zimmer',
          commute_mode: 'Pendelmodus',
          distance_per_trip_km: 'Entfernung pro Fahrt (km)',
          commute_days_per_year: 'Pendeltage pro Jahr',
          num_commuters: 'Anzahl der Pendler',
          vehicle_miles: 'Fahrzeugmeilen',
          short_ton_miles: 'Tonnenmeilen',
          waste_type: 'Abfallart',
          disposal_method: 'Entsorgungsmethode',
          amount: 'Menge',
          offset_description: 'Projektbeschreibung',
          scope_category: 'Scope/Kategorie',
          amount_mtco2e: 'Gekaufte Offsets (MT CO2e)',
          project_description: 'Projektbeschreibung',
          flightLength: 'Fluglänge'
        },
        recordAmendment: 'Datensatz-Korrektur',
          newEnvironmentalEntry: 'Neuer Umwelt-Eintrag',
          reportingContext: 'Berichtskontext',
          targetAuditPeriod: 'Ziel-Auditzeitraum',
          selectedPeriod: 'Ausgewählter Zeitraum',
          autoAssigned: 'Automatisch zugewiesen',
          noReportingPeriodError: 'Kein Berichtszeitraum angegeben. Bitte navigieren Sie über "Aktive Berichte", um Aktivitäten hinzuzufügen.',
          primaryMetricData: 'Primäre Metrikdaten',
          saveActivity: 'Aktivität speichern',
          commitChanges: 'Änderungen speichern',
          persistRecord: 'Eintrag speichern',
          auditLabel: 'Umwelt-Audit',
          operationalTitle: 'operative Aktivitäten.',
          vehicleType: 'Fahrzeugtyp',
          vehicleMiles: 'Fahrzeugmeilen',
          shortTonMiles: 'Short-Ton-Meilen',
          distanceKm: 'Entfernung (km)',
          weightTons: 'Gewicht (Tonnen)',
          offsetId: 'ID',
          offsetsPurchased: 'Gekaufte Offsets (MT CO₂e)',
          egridSubregion: 'eGRID-Unterregion',
          kwhPurchased: 'Gekaufte kWh',
          fuelType: 'Kraftstofftyp',
          efficiencyPct: 'Effizienz (%)',
          purchasedMMBtu: 'Gekauft (MMBtu)',
          shipment: 'Sendung',
          transportMode: 'Modus',
          activityDescription: 'Beschreibung',
          backToChecklist: 'Zurück zur Aktivitätsliste',
          inventoryChangeKg: 'Bestandsveränderung (kg)',
          transferredKg: 'Übertragen (kg)',
          capacityChangeKg: 'Kapazitätsveränderung (kg)',
          newChargeKg: 'Neue Ladung (kg)',
          newCapacityKg: 'Neue Kapazität (kg)',
          rechargeKg: 'Nachladung (kg)',
          equipmentType: 'Gerätetyp',
          opCapacityKg: 'Betr. Kapazität (kg)',
          inventoryChangeLb: 'Bestandsveränderung (lb)',
          transferredLb: 'Übertragen (lb)',
          capacityChangeLb: 'Kapazitätsveränderung (lb)',
          newChargeLb: 'Neue Ladung (lb)',
          rechargeLb: 'Nachladung (lb)',
          disposedCapLb: 'Entsorg. Kap. (lb)',
          recoveredLb: 'Zurückgewonnen (lb)',
          capLb: 'Kapazität (lb)',
          purchasedAmountLb: 'Gekaufte Menge (lb)',
          hotelName: 'Hotelname',
          hotelCategory: 'Kategorie',
          nights: 'Nächte',
          rooms: 'Zimmer',
          commuteMode: 'Pendelart',
          numCommuters: 'Pendler',
          distancePerTripKm: 'Entfernung/Fahrt (km)',
          distanceMiles: 'Entfernung (Meilen)',
          flightLength: 'Fluglänge',
          wasteType: 'Abfalltyp',
          disposalMethod: 'Entsorgungsmethode',
          amount: 'Menge',
          gasType: 'Gastyp',
          projectDescription: 'Projektbeschreibung',
          scopeCategory: 'Bereich / Kategorie',
          createSuccess: 'Aktivit\u00e4t erfolgreich gespeichert',
          updateSuccess: 'Aktivit\u00e4t erfolgreich aktualisiert',
          createFailed: 'Aktivit\u00e4t konnte nicht erstellt werden',
          updateFailed: 'Aktivit\u00e4t konnte nicht aktualisiert werden',
          missingFields: 'Pflichtfelder fehlen: {{fields}}',
          currentCategory: 'Betriebliche Kategorie',
          selectType: 'Betrieblichen Datenstrom ausw\u00e4hlen',
          boundarySettingsInfo: 'Berichtsgrenzen aktiv.',
          editSettings: 'Konfigurieren',
          notFound: 'Aktivit\u00e4t nicht gefunden',
          vehicleYear: 'Baujahr',
          fuelCombusted: 'Verbrannter Brennstoff',
          fuelState: 'Aggregatzustand',
          quantityCombusted: 'Verbrannte Menge',
          units: 'Einheiten',
          co2Equivalent: 'CO\u2082-\u00c4quivalent'
      },
      users: {
        title: 'Benutzerverwaltung',
        subtitle: 'Teammitglieder und Berechtigungen verwalten',
        inviteUser: 'Benutzer einladen',
        addUser: 'Benutzer hinzufügen',
        firstName: 'Vorname',
        lastName: 'Nachname',
        company: 'Firma',
        password: 'Passwort',
        name: 'Name',
        email: 'E-Mail',
        role: 'Rolle',
        lastLogin: 'Letzte Anmeldung',
        noUsers: 'Keine Benutzer gefunden',
        confirmRemove: 'Möchten Sie diesen Benutzer wirklich entfernen?',
        confirmDeactivate: 'Sind Sie sicher, dass Sie diesen Benutzer deaktivieren möchten?',
        deactivateSuccess: 'Benutzer erfolgreich deaktiviert',
        reactivateSuccess: 'Benutzer erfolgreich reaktiviert',
        removeSuccess: 'Benutzer erfolgreich entfernt',
        roleUpdateSuccess: 'Benutzerrolle erfolgreich aktualisiert',
        inviteSuccess: 'Benutzereinladung erfolgreich gesendet',
        sendInvite: 'Einladung senden',
        confirmDelete: 'Möchten Sie diesen Benutzer wirklich dauerhaft aus dem Unternehmen entfernen?',
        deleteSuccess: 'Benutzer erfolgreich entfernt',
        failedLoad: 'Benutzer konnten nicht geladen werden',
        userAddedSuccess: 'Benutzer hinzugefügt und Zugangsdaten per E-Mail gesendet',
        failedAdd: 'Benutzer konnte nicht hinzugefügt werden',
        failedUpdateRole: 'Rolle konnte nicht aktualisiert werden',
        failedDeactivate: 'Benutzer konnte nicht deaktiviert werden',
        failedDelete: 'Benutzer konnte nicht gelöscht werden',
        failedReactivate: 'Benutzer konnte nicht reaktiviert werden',
        roleViewer: 'Betrachter - Nur Lesezugriff',
        roleEditor: 'Editor - Kann Daten hinzufügen/bearbeiten',
        roleAdmin: 'Admin - Vollzugriff',
        minPassword: 'Min. 8 Zeichen',
        roles: {
          company_admin: 'Unternehmensadmin',
          editor: 'Editor',
          viewer: 'Betrachter',
          internal_admin: 'Interner Admin'
        }
      },
      boundary: {
        welcomeTitle: 'Willkommen zur Grenzdefinition',
        welcomeDesc: 'Lassen Sie uns bestimmen, welche Emissionsquellen für Ihr Unternehmen relevant sind. So können wir die Plattform auf Ihre spezifischen Bedürfnisse zuschneiden.',
        chooseIndustry: 'Wählen Sie Ihre Branche (Optional)',
        chooseIndustryDesc: 'Wählen Sie Ihre Branche für empfohlene Einstellungen oder überspringen Sie dies und passen Sie manuell an.',
        skipManual: 'Überspringen und manuell anpassen',
        scope1Title: 'Scope 1: Direkte Emissionen',
        scope1Desc: 'Emissionen aus Quellen, die von Ihrem Unternehmen kontrolliert werden.',
        scope23Title: 'Scope 2 & 3: Indirekte Emissionen',
        scope23Desc: 'Emissionen aus zugekaufter Energie und Ihrer Wertschöpfungskette.',
        scope2SectionTitle: 'Scope 2: Energieindirekt',
        scope3SectionTitle: 'Scope 3: Wertschöpfungskette',
        offsetsSectionTitle: 'CO2-Kompensationen',
        reviewTitle: 'Auswahl überprüfen',
        reviewDesc: 'Hier ist eine Zusammenfassung Ihrer organisatorischen Grenzen. Sie können zurückgehen, um Änderungen vorzunehmen.',
        noScope1: 'Keine Scope 1-Module ausgewählt',
        noScope2: 'Keine Scope 2-Module ausgewählt',
        noScope3: 'Keine Scope 3-Module ausgewählt',
        offsetEnabled: 'Kompensationsverfolgung aktiviert',
        offsetDisabled: 'Kompensationsverfolgung deaktiviert',
        noteUpdate: 'Hinweis: Sie können diese Einstellungen später über die Seite "Berichtszeiträume" aktualisieren.',
        examples: 'Beispiele:',
        stepIndustry: 'Branche',
        stepScope1: 'Scope 1',
        stepScope23: 'Scope 2 & 3',
        stepReview: 'Überprüfung',
        saving: 'Wird gespeichert...',
        saveFinish: 'Speichern und abschließen',
        back: 'Zurück',
        next: 'Weiter',
        activityTypeLabel: 'Aktivitätstyp:',
        yes: 'JA',
        no: 'NEIN',
        requiredLabel: 'Erforderlich',
        orgTitle: 'Organisatorische Grenzeinstellungen',
        orgDesc: 'Konfigurieren Sie, welche Emissionskategorien für Ihre Organisation gelten. Diese Einstellungen filtern die verfügbaren Aktivitätstypen.',
        loadError: 'Grenzeinstellungen konnten nicht geladen werden',
        saveSuccess: 'Grenzeinstellungen erfolgreich gespeichert',
        saveError: 'Grenzeinstellungen konnten nicht gespeichert werden',
        saveBtn: 'Grenzeinstellungen speichern',
        howItWorks: 'Wie Grenzeinstellungen funktionieren',
        howItem1: 'Erforderliche Fragen sind immer aktiviert und können nicht geändert werden (Scope 1 & Scope 2)',
        howItem2: 'Scope 3-Fragen können je nach Ihren organisatorischen Grenzen aktiviert/deaktiviert werden',
        howItem3: 'Aktivitäten erscheinen nur im System, wenn die entsprechende Grenzfrage mit "JA" beantwortet wurde',
        howItem4: 'Änderungen werden sofort nach dem Speichern wirksam',
      },
      activityDetails: {
        activityRecord: 'Aktivitätsdatensatz',
        entryDetails: 'Eintragsdetails',
        totalFootprint: 'Gesamtfußabdruck',
        created: 'Erstellt',
        lastUpdated: 'Zuletzt aktualisiert',
      },
      help: {
        standards: 'Standards & Rahmenwerke',
        scopes: 'Emissionsbereiche (Scopes)',
        terms: 'Glossar',
        activities: 'Aktivitätstypen',
        dataQuality: 'Datenqualität',
        title: 'Hilfe & Dokumentation',
        subtitle: 'Erfahren Sie mehr über Emissionsberichtsstandards, Terminologie und Best Practices',
        topics: 'Themen',
        standardsTitle: 'Berichtsstandards & Rahmenwerke',
        whoMustComply: 'Wer muss es einhalten?',
        keyRequirements: 'Wichtige Anforderungen',
        keyPrinciples: 'Wichtige Grundsätze',
        timelineLabel: 'Zeitplan:',
        scopesTitle: 'Emissionsbereiche erklärt',
        categories: 'Kategorien',
        commonExamples: 'Häufige Beispiele',
        whyItMatters: 'Warum es wichtig ist',
        glossaryTitle: 'Glossar der Begriffe',
        gwpValues: 'Häufige GWP-Werte (100-Jahre)',
        trustedSources: 'Verlässliche Quellen',
        activityGuide: 'Leitfaden für Aktivitätstypen',
        dataNeed: 'Benötigte Daten:',
        tipsLabel: 'Tipps:',
        dataQualityLevels: 'Datenqualitätsstufen',
        qualityLabel: 'Qualität:',
        examplesLabel: 'Beispiele:',
        improveTips: 'Tipps zur Verbesserung der Datenqualität',
        loadingContent: 'Hilfeinhalt wird geladen...',
        failedLoad: 'Hilfeinhalt konnte nicht geladen werden',
      },
      reports: {
        title: 'Berichte',
        subtitle: 'Emissionsberichte erstellen und anzeigen',
        generateReport: 'Bericht erstellen',
        reportName: 'Berichtsname',
        type: 'Typ',
        period: 'Zeitraum',
        generatedAt: 'Erstellt am',
        status: 'Status',
        download: 'Herunterladen',
        noReports: 'Keine Berichte gefunden',
        viewReport: 'Bericht anzeigen',
        totalEmissions: 'Gesamtemissionen',
        scope1: 'Scope 1',
        scope2: 'Scope 2',
        scope3: 'Scope 3',
        scope1Emissions: 'Scope 1 Emissionen',
        scope2Emissions: 'Scope 2 Emissionen',
        scope3Emissions: 'Scope 3 Emissionen',
        emissionsTrends: 'Emissionstrends',
        scopeBreakdown: 'Scope-Aufschlüsselung',
        mtCO2e: 'MT CO2e',
        generateSuccess: 'Bericht erfolgreich erstellt',
        generateSubtitle: 'Erstellen Sie umfassende Berichte für Ihre Stakeholder und Audits',
        company: 'Unternehmen',
        timeline: 'Zeitraum',
        selectPeriod: 'Berichtszeitraum auswählen',
        loadingPeriods: 'Zeiträume werden geladen...',
        noPeriods: 'Keine Berichtszeiträume gefunden.',
        managePeriods: 'Berichtszeiträume verwalten',
        selectPeriodHint: 'Bitte wählen Sie einen Berichtszeitraum aus, für den ein Bericht erstellt werden soll.',
        reportStatus: 'Berichtstatus',
        reportPeriodLabel: 'Berichtszeitraum',
        paid: 'Bezahlt',
        generatedStatus: 'Erstellt',
        fullPdfExport: 'Vollständiger PDF-Export',
        pdfExportDesc: 'Beinhaltet CSRD-Compliance, Prüfpfade und Multi-Format-Exporte.',
        unlockAccess: 'Vollzugang freischalten →',
        oneTimePayment: 'Einmalzahlung. Mehrbenutzerzugang inklusive.',
        downloadPdf: 'PDF herunterladen',
        downloadCsv: 'CSV herunterladen',
        reportDetails: 'Berichtsdetails',
        standardLabel: 'Standard',
        includeCharts: 'Diagramme einbeziehen',
        detailedBreakdown: 'Detaillierte Aufschlüsselung',
        loadingData: 'Daten werden geladen...',
        awaitingData: 'Warte auf Daten',
        addActivitiesHint: 'Fügen Sie Aktivitäten hinzu, um Ihre Emissionszusammenfassung zu erstellen.',
        directLabel: 'Direkt',
        indirectEnergy: 'Indirekte Energie',
        contractual: 'Vertraglich',
        supplyChainLabel: 'Lieferkette',
        totalImpact: 'Gesamtauswirkung',
        confidential: 'Vertraulich',
        emissionsReport: 'Emissionsbericht',
        liveDraft: 'Live-Entwurf',
        activityBreakdown: 'Emissionen nach Aktivitätstyp',
        activityType: 'Aktivitätstyp',
        emissionsCol: 'Emissionen (MT CO₂e)',
        countCol: 'Anzahl',
        sustainabilityScore: 'Nachhaltigkeitsbewertung',
        currentRating: 'Aktuelle Bewertung',
        goodPerformance: 'Gute Nachhaltigkeitsleistung',
        roomForImprovementText: 'Verbesserungspotenzial',
        needsAttention: 'Benötigt sofortige Aufmerksamkeit',
        keyRecommendations: 'Wesentliche Empfehlungen',
        auditTrail: 'Prüfpfad',
        certificationSection: 'Zertifizierung',
        signedBy: 'Unterzeichnet von:',
        reportDate: 'Datum:',
        history: 'Berichtshistorie',
        historySubtitle: 'Ihre erstellten Berichte anzeigen und herunterladen',
        generateNew: 'Neuen Bericht erstellen',
        filterByPeriod: 'Nach Zeitraum filtern:',
        allPeriods: 'Alle Zeiträume',
        noReportsFound: 'Keine Berichte gefunden',
        noReportsAll: 'Sie haben noch keine Berichte erstellt.',
        noReportsPeriod: 'Keine Berichte für den ausgewählten Zeitraum gefunden.',
        generateFirst: 'Ersten Bericht erstellen',
        totalReports: 'Berichte gesamt',
        pdfReports: 'PDF-Berichte',
        csvReports: 'CSV/Excel-Berichte',
        totalSpent: 'Gesamt ausgegeben',
        deleteReport: 'Löschen',
        deleteConfirmMsg: 'Möchten Sie diesen Bericht wirklich löschen?',
        loadingHistory: 'Berichtshistorie wird geladen...',
        colGeneratedDate: 'Erstellungsdatum',
        colPeriod: 'Berichtszeitraum',
        colType: 'Typ',
        colPayment: 'Zahlung',
        colAmount: 'Betrag',
        colActions: 'Aktionen',
        statusPaid: 'Bezahlt',
        statusPending: 'Ausstehend',
        statusFailed: 'Fehlgeschlagen',
        statusRefunded: 'Erstattet',
        notFound: 'Bericht nicht gefunden',
        paymentSuccess: 'Zahlung erfolgreich! Ihr Bericht ist bereit.',
        paymentCancelled: 'Zahlung wurde abgebrochen.',
        downloadSuccess: '{{format}} erfolgreich heruntergeladen',
        paymentRequired: 'Zahlung erforderlich, um diesen Bericht herunterzuladen',
        paymentProcessingRetry: 'Die Zahlung wird möglicherweise noch verarbeitet. Bitte versuchen Sie es in Kürze erneut.',
      },
      messages: {
        success: 'Erfolgreich!',
        error: 'Ein Fehler ist aufgetreten',
        loading: 'Lädt...',
        noData: 'Keine Daten verfügbar',
        confirmDelete: 'Möchten Sie dies wirklich löschen?'
      },
      validation: {
        required: 'Dieses Feld ist erforderlich',
        invalidEmail: 'Ungültige E-Mail-Adresse',
        passwordMismatch: 'Passwörter stimmen nicht überein',
        minLength: 'Mindestens {{count}} Zeichen erforderlich',
        maxLength: 'Maximal {{count}} Zeichen erlaubt'
      },
      status: {
        active: 'Aktiv',
        locked: 'Gesperrt',
        archived: 'Archiviert',
        pending: 'Ausstehend',
        approved: 'Genehmigt',
        rejected: 'Abgelehnt',
        completed: 'Abgeschlossen',
        in_progress: 'In Bearbeitung',
        calculated: 'Berechnet',
        draft: 'Entwurf',
        closed: 'Geschlossen'
      },
      reportingPeriods: {
        title: 'Aktive Berichte',
        subtitle: 'Verwalten Sie Ihre Berichtszeiträume',
        createNew: 'Neuen Bericht erstellen',
        label: 'Bericht Label',
        labelPlaceholder: 'z.B. Geschäftsjahr 2024',
        startDate: 'Startdatum',
        endDate: 'Enddatum',
        status: 'Status',
        createSuccess: 'Bericht erfolgreich erstellt',
        emptyStateTitle: 'Willkommen bei CalculateCO2!',
        emptyStateText: 'Um Ihre ESG-Reise zu beginnen, müssen Sie Ihren ersten <strong>Berichtszeitraum</strong> einrichten (z. B. Geschäftsjahr 2024).',
        createButton: 'Berichtszeitraum erstellen',
        deleteConfirm: 'Sind Sie sicher, dass Sie "{{label}}" löschen möchten? Alle zugehörigen Daten gehen unwiderruflich verloren.',
        deleteSuccess: 'Bericht erfolgreich gelöscht',
        deleteError: 'Fehler beim Löschen des Berichts',
        createError: 'Fehler beim Erstellen des Berichtszeitraums',
        loadError: 'Fehler beim Laden der Berichte',
        noPeriods: 'Keine aktiven Berichte',
        noPeriodsDescription: 'Erstellen Sie Ihren ersten Berichtszeitraum, um die CO2-Emisionen Ihres Unternehmens zu verfolgen.',
        create: '+ Bericht erstellen',
        description: 'Verwalten Sie Ihre organisatorischen Berichtszeiträume für die CO2-Bilanzierung',
        standard: 'Standard',
        type: 'Typ',
        newPeriod: 'Neuer Bericht',
        noActiveReports: 'Keine aktiven Berichte',
        noActiveDesc: 'Erstellen Sie Ihren ersten Berichtszeitraum, um die CO₂-Emissionen Ihres Unternehmens zu verfolgen.',
        loadingReports: 'Berichte werden geladen...',
        editBoundaryTooltip: 'Grenzfragen bearbeiten',
        deleteReportTooltip: 'Bericht löschen'
      },
      period: {
        types: {
          annual: 'Jährlich',
          quarterly: 'Vierteljährlich',
          monthly: 'Monatlich',
          custom: 'Benutzerdefiniert'
        }
      },
      standards: {
        csrd: {
          shortDesc: 'Verpflichtend für EU-Unternehmen, die Größenkriterien erfüllen',
          note: 'Hinweis:',
          mandatoryInfo: 'Die CSRD ist verpflichtend für EU-Unternehmen mit ≥250 Mitarbeitern, ≥50 Mio. € Umsatz oder ≥25 Mio. € Bilanzsumme. Nicht-EU-Unternehmen können das GHG Protocol oder ISO 14064 verwenden.'
        },
        ghg: {
          shortDesc: 'Globaler freiwilliger Standard für die Emissionsberichterstattung'
        },
        iso: {
          shortDesc: 'Internationaler Standard für die GHG-Quantifizierung'
        },
        compareStandards: 'Standards vergleichen',
        comparison: {
          feature: 'Merkmal',
          region: 'Region',
          typeLabel: 'Typ',
          scope3Required: 'Scope 3 erforderlich',
          assurance: 'Prüfung',
          bestFor: 'Am besten für',
          mandatory: 'Pflicht (EU)',
          voluntary: 'Freiwillig',
          yes: 'Ja',
          optional: 'Optional',
          partial: 'Teilweise',
          required: 'Erforderlich',
          recommended: 'Empfohlen',
          eu: 'EU',
          global: 'Global',
          euCompanies: 'EU-Unternehmen',
          globalReporting: 'Globale Berichterstattung',
          isoCert: 'ISO-Zertifizierung',
          recommendation: '💡 Empfehlung',
          euSMEsTitle: 'Für EU-KMU:',
          euSMEsText: 'Beginnen Sie mit CSRD, da es verpflichtend wird. CalculateCO2 ist so konzipiert, dass es CSRD-Anforderungen standardmäßig erfüllt.',
          globalTitle: 'Für globale Unternehmen:',
          globalText: 'Das GHG-Protokoll bietet maximale Flexibilität und ist international weit anerkannt.',
          overview: 'Überblick',
          whoMustComply: 'Wer muss es einhalten',
          keyRequirements: 'Wichtige Anforderungen',
          keyPrinciples: 'Wichtige Grundsätze',
          timeline: '⏱️ Zeitplan',
        }
      },
      checklist: {
        backToReports: 'Zurück zu den Berichten',
        activities: 'Aktivitäten',
        configureSources: 'Konfigurieren Sie unten die Daten für jede aktivierte Emissionsquelle.',
        editQuestions: 'Fragen bearbeiten',
        generateReport: 'Bericht erstellen',
        noActivitiesEnabled: 'Keine Aktivitäten aktiviert',
        noActivitiesDesc: 'Sie müssen die Fragen zu den Grenzen beantworten, um Emissionsquellen für die Verfolgung zu aktivieren.',
        goToQuestions: 'Zu den Fragen',
        readyTitle: 'Bereit, Ihren Bericht zu erstellen?',
        readyDesc: 'Überprüfen Sie Ihre Einträge und sehen Sie Ihren gesamten CO2-Fußabdruck, bevor Sie den endgültigen Konformitätsbericht erstellen.',
        fetchError: 'Aktivitäten konnten nicht geladen werden',
        sources: 'Quellen'
      },
      activityMetadata: {
        stationary_combustion: { label: 'Stationäre Verbrennung', description: 'Verbrennung von Brennstoffen vor Ort (Kessel, Öfen)' },
        mobile_sources: { label: 'Mobile Quellen', description: 'Eigene oder geleaste Fahrzeuge' },
        refrigeration_ac_material_balance: { label: 'Kältetechnik & Klima', description: 'Diffuse Emissionen aus Kühlsystemen' },
        refrigeration_ac_simplified_material_balance: { label: 'Kältetechnik & Klima (Vereinfacht)', description: 'Diffuse Emissionen' },
        refrigeration_ac_screening_method: { label: 'Kältetechnik & Klima (Screening)', description: 'Diffuse Emissionen' },
        fire_suppression_material_balance: { label: 'Brandbekämpfung', description: 'Emissionen aus Feuerlöschmitteln' },
        fire_suppression_simplified_material_balance: { label: 'Brandbekämpfung (Vereinfacht)', description: 'Feuerlöschmittel' },
        fire_suppression_screening_method: { label: 'Brandbekämpfung (Screening)', description: 'Feuerlöschmittel' },
        purchased_gases: { label: 'Gekaufte Gase', description: 'In Prozessen verwendete Industriegase' },
        electricity: { label: 'Gekaufter Strom', description: 'Gekaufter Strom für den Eigenverbrauch' },
        steam: { label: 'Gekaufter Dampf', description: 'Gekaufter Dampf, Wärme oder Kälte' },
        business_travel_personal_car: { label: 'Geschäftsreisen (Auto)', description: 'Reisen mit dem eigenen Fahrzeug der Mitarbeiter' },
        business_travel_rail_bus: { label: 'Geschäftsreisen (Bahn/Bus)', description: 'Geschäftsreisen mit öffentlichen Verkehrsmitteln' },
        business_travel_air: { label: 'Geschäftsreisen (Flug)', description: 'Flugreisen für geschäftliche Zwecke' },
        business_travel_hotel: { label: 'Hotelübernachtungen', description: 'Geschäftliche Hotelunterkünfte' },
        employee_commuting_personal_car: { label: 'Pendeln der Mitarbeiter (Auto)', description: 'Pendeln mit Privatfahrzeugen' },
        employee_commuting_public_transport: { label: 'Pendeln der Mitarbeiter (ÖPNV)', description: 'Pendeln mit öffentlichen Verkehrsmitteln' },
        upstream_trans_dist_vehicle_miles: { label: 'Vorgelagerter Transport & Verteilung', description: 'Transport von gekauften Waren' },
        upstream_trans_dist_ton_miles: { label: 'Vorgelagerter Transport & Verteilung (Tonnenmeilen)', description: 'Großflächiger Transport' },
        waste: { label: 'Abfallerzeugung', description: 'Entsorgung und Behandlung von Abfällen' },
        offsets: { label: 'CO2-Kompensationen', description: 'Verifizierte Emissionsreduzierungen' }
      },

      reportingStandards: {
        GHG_PROTOCOL: 'GHG Protocol',
        CSRD: 'CSRD',
        ISO_14064: 'ISO 14064'
      },
      landing: {
        hero: {
          badge: 'CSRD & ESG konform',
          title: 'KI-gestützt',
          subtitle: 'ESG-Compliance',
          titleSuffix: 'leicht gemacht',
          description: 'Automatisieren Sie Ihre CO2-Fußabdruck-Berechnungen und ESG-Berichterstattung mit der Genauigkeit der Europäischen Umweltagentur. Entwickelt für KMU, die CSRD-Anforderungen erfüllen müssen.',
          ctaStart: 'Kostenlos testen',
          ctaDemo: 'Demo ansehen',
          ctaDashboard: 'Zum Dashboard',
          trustCompanies: 'Unternehmen',
          trustAccuracy: 'Genauigkeit',
          trustSupport: 'Support'
        },
        features: {
          badge: 'Plattform-Funktionen',
          title: 'Alles, was Sie brauchen für',
          titleHighlight: ' ESG-Exzellenz',
          description: 'Speziell für europäische Unternehmen entwickelt, um CSRD-Anforderungen effizient und genau zu erfüllen.',
          automated: {
            title: 'Automatisierte Berechnungen',
            desc: 'Geben Sie Ihre Aktivitätsdaten ein und unsere KI berechnet automatisch CO₂e unter Verwendung der neuesten EEA-Emissionsfaktoren mit höchster Präzision.'
          },
          csrd: {
            title: 'CSRD-konforme Berichte',
            desc: 'Erstellen Sie im Handumdrehen prüfungsfähige Berichte, die den Standards der Corporate Sustainability Reporting Directive entsprechen.'
          },
          scope: {
            title: 'Vollständige Scope-Abdeckung',
            desc: 'Umfassende Verfolgung von Scope 1, 2 & 3 Emissionen einschließlich Berechnungsmodulen für die Lieferkette.'
          }
        },
        howItWorks: {
          badge: 'Einfacher Prozess',
          title: 'Starten in ',
          titleHighlight: 'drei Schritten',
          description: 'Von der Anmeldung zum Compliance-Bericht in Minuten, nicht Monaten.',
          step1: {
            title: 'Konto erstellen',
            desc: 'Melden Sie sich an und richten Sie Ihr Unternehmensprofil ein. Definieren Sie Berichtsgrenzen mit unserem geführten Fragebogen.'
          },
          step2: {
            title: 'Aktivitäten erfassen',
            desc: 'Protokollieren Sie Ihre Geschäftsaktivitäten ’ Energie, Reisen, Abfall. Unsere KI ordnet automatisch die richtigen Emissionsfaktoren zu.'
          },
          step3: {
            title: 'Berichte erstellen',
            desc: 'Erhalten Sie sofort CSRD-konforme Berichte mit umsetzbaren Erkenntnissen zur Emissionsreduzierung und zum Nachweis der Compliance.'
          }
        },
        about: {
          badge: 'Über CalculateCO2',
          title: 'Nachhaltige',
          titleHighlight: ' Geschäftsentscheidungen stärken',
          desc1: 'CalculateCO2 ist ein Climate-Tech-Unternehmen mit der Mission, KMU in ganz Europa dabei zu unterstützen, die Kontrolle über ihren CO2-Fußabdruck zu übernehmen. Unser Team vereint Fachwissen in den Bereichen Nachhaltigkeit, Datenwissenschaft und Softwareentwicklung.',
          desc2: 'Wir glauben, dass jedes Unternehmen, unabhängig von seiner Größe, Zugang zu genauen Werkzeugen zur Messung, Verwaltung und Reduzierung von Umweltauswirkungen verdient. CalculateCO2 setzt auf Transparenz, Innovation und die Unterstützung des Übergangs zu einer kohlenstoffarmen Wirtschaft.',
          stats: {
            emissions: 'Verfolgte Emissionen',
            satisfaction: 'Kundenzufriedenheit',
            countries: 'EU-Länder',
            savings: 'Durchschn. Kosteneinsparung'
          }
        },
        cta: {
          title: 'Bereit, Ihre',
          titleHighlight: ' ESG-Compliance zu vereinfachen?',
          description: 'Schließen Sie sich hunderten europäischen KMU an, die bereits CalculateCO2 nutzen, um ihre CO2-Berichterstattung zu automatisieren und CSRD-Anforderungen sicher zu erfüllen.',
          ctaTrial: 'Starten Sie Ihre kostenlose Testphase',
          ctaDemo: 'Demo vereinbaren',
          ctaDashboard: 'Zum Dashboard'
        },
        footer: {
          rights: '© {{year}} CalculateCO2. Alle Rechte vorbehalten.',
          features: 'Funktionen',
          howItWorks: 'So funktioniert es',
          about: 'Über uns'
        }
      },
      activityTypes: {
        mobile_combustion: 'Mobile Verbrennung',
        stationary_combustion: 'Stationäre Verbrennung',
        purchased_electricity: 'Gekaufter Strom',
        transportation: 'Transport',
        waste_generated: 'Abfallaufkommen',
        business_travel: 'Geschäftsreisen',
        employee_commuting: 'Mitarbeiterpendeln',
        leased_assets: 'Geleaste Vermögenswerte',
        capital_goods: 'Investitionsgüter',
        mobile_sources: 'Mobile Quellen',
        refrigeration_ac: 'Kälte- & Klimaanlagen',
        refrigeration_ac_material_balance: 'Kälte- & Klimaanlagen (Materialbilanz)',
        refrigeration_ac_simplified_material_balance: 'Kälte- & Klimaanlagen (Vereinfacht)',
        refrigeration_ac_screening_method: 'Kälte- & Klimaanlagen (Screening)',
        fire_suppression: 'Feuerlöschsysteme',
        fire_suppression_material_balance: 'Feuerlöschsysteme (Materialbilanz)',
        fire_suppression_simplified_material_balance: 'Feuerlöschsysteme (Vereinfacht)',
        fire_suppression_screening_method: 'Feuerlöschsysteme (Screening)',
        purchased_gases: 'Gekaufte Gase',
        electricity: 'Strom',
        steam: 'Dampf/Heizung/Kühlung',
        business_travel_air: 'Geschäftsreisen - Flug',
        business_travel_rail: 'Geschäftsreisen - Bahn',
        business_travel_road: 'Geschäftsreisen - Straße',
        business_travel_hotel: 'Geschäftsreisen - Hotel',
        business_travel_personal_car: 'Geschäftsreisen - Eigenes Fahrzeug',
        business_travel_rail_bus: 'Geschäftsreisen - Bahn oder Bus',
        commuting: 'Mitarbeiterpendeln',
        employee_commuting_personal_car: 'Mitarbeiterpendeln - Privat-PKW',
        employee_commuting_public_transport: 'Mitarbeiterpendeln - ÖPNV',
        transportation_distribution: 'Transport & Logistik',
        upstream_trans_dist_vehicle_miles: 'Vorgelagerter Transport - Meilen',
        upstream_trans_dist_ton_miles: 'Vorgelagerter Transport - Tonnenmeilen',
        waste: 'Abfallentsorgung',
        offsets: 'CO2-Kompensation'
      },
      scopes: {
        Scope1: 'Bereich 1',
        Scope2: 'Bereich 2',
        Scope3: 'Bereich 3',
        Other: 'Sonstiges'
      },
      fields: {
        source_id: 'Quellen-ID',
        source_description: 'Quellenbeschreibung',
        source_area_sqft: 'Quellfläche (qf)',
        fuel_combusted: 'Verbrannter Brennstoff',
        fuel_state: 'Aggregatzustand',
        quantity_combusted: 'Verbrannte Menge',
        units: 'Einheiten',
        on_road_or_non_road: 'Straße oder Geländefahrzeug?',
        vehicle_type: 'Fahrzeugtyp',
        vehicle_year: 'Baujahr',
        fuel_usage: 'Kraftstoffverbrauch',
        miles_traveled: 'Gefahrene Meilen',
        refrigerant_type: 'Gas',
        gwp: 'Treibhauspotenzial (GWP)',
        inventory_change: 'Bestandsveränderung (kg)',
        transferred_amount: 'Übertragene Menge (kg)',
        capacity_change: 'Kapazitätsänderung (kg)',
        new_units_charge: 'Neue Einheiten – Befüllung (kg)',
        new_units_capacity: 'Neue Einheiten – Kapazität (kg)',
        existing_units_recharge: 'Vorhandene Einheiten – Wiederbefüllung (kg)',
        disposed_units_capacity: 'Entsorgte Einheiten – Kapazität (kg)',
        disposed_units_recovered: 'Entsorgte Einheiten – Rückgewinnung (kg)',
        equipment_type: 'Gerätetyp',
        operating_units_capacity: 'Betriebskapazität (kg)',
        months_in_operation: 'Betriebsmonate',
        facility_description: 'Anlagenbeschreibung',
        suppressant_type: 'Löschmitteltyp',
        amount_used: 'Verwendete Menge',
        amount_units: 'Einheiten',
        inventory_change_lb: 'Bestandsveränderung (lb)',
        transferred_amount_lb: 'Übertragene Menge (lb)',
        capacity_change_lb: 'Kapazitätsänderung (lb)',
        new_units_charge_lb: 'Neue Einheiten – Befüllung (lb)',
        new_units_capacity_lb: 'Neue Einheiten – Kapazität (lb)',
        existing_units_recharge_lb: 'Vorhandene Einheiten – Wiederbefüllung (lb)',
        disposed_units_capacity_lb: 'Entsorgte Einheiten – Kapazität (lb)',
        disposed_units_recovered_lb: 'Entsorgte Einheiten – Rückgewinnung (lb)',
        unit_capacity_lb: 'Einheitskapazität (lb)',
        gas_type: 'Gastyp',
        amount_purchased: 'Gekaufte Menge',
        co2e: 'CO2-Äquivalent (lb)',
        facility_location: 'eGRID-Teilregion',
        kwh_purchased: 'Gekaufter Strom (kWh)',
        market_based_co2_factor: 'Marktbasierter CO2-Faktor (lb/MWh)',
        market_based_ch4_factor: 'Marktbasierter CH4-Faktor (lb/MWh)',
        market_based_n2o_factor: 'Marktbasierter N2O-Faktor (lb/MWh)',
        fuel_type: 'Brennstoffart',
        boiler_efficiency: 'Kesselwirkungsgrad (%)',
        location_based_co2_factor: 'Standortbasierter CO2-Faktor (kg/MMBtu)',
        location_based_ch4_factor: 'Standortbasierter CH4-Faktor (g/MMBtu)',
        location_based_n2o_factor: 'Standortbasierter N2O-Faktor (g/MMBtu)',
        vehicle_miles: 'Fahrzeugmeilen',
        short_ton_miles: 'Kurztonnenmeilen',
        hotel_name: 'Hotelname',
        city_country: 'Stadt/Land',
        hotel_category: 'Hotelkategorie',
        num_nights: 'Anzahl der Nächte',
        num_rooms: 'Anzahl der Zimmer',
        commute_mode: 'Pendelart',
        distance_per_trip_km: 'Distanz pro Fahrt (km)',
        commute_days_per_year: 'Pendeltage pro Jahr',
        num_commuters: 'Anzahl der Pendler',
        waste_type: 'Abfallmaterial',
        disposal_method: 'Entsorgungsmethode',
        amount: 'Gewicht',
        offset_description: 'Projektbeschreibung',
        scope_category: 'Kompensationsbereich/-kategorie',
        amount_mtco2e: 'Gekaufte Kompensationen (MT CO₂e)'
      },
      validation: {
        required: 'Dieses Feld ist erforderlich',
        invalidEmail: 'Ungültige E-Mail-Adresse',
        minLength: 'Mindestens {{count}} Zeichen erforderlich',
        passwordMismatch: 'Passwörter stimmen nicht überein',
        endDateAfterStart: 'Das Enddatum muss nach dem Startdatum liegen'
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    react: {
      useSuspense: false,
    },
  });

export default i18n;
